import * as React from "react";
import Draggable from "react-draggable";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  PaperProps,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import type { CvJson } from "../../shared/types/cvJson";
import type { FirefightClip } from "./GameStory_firefights";

type Team01 = 0 | 1;
type PlayerId = 0 | 1 | 2 | 3;
type Role = string;
type EvidenceKind = "created" | "prevented";

type OpenShotPreviewPayload = {
  row: KitchenArrivalRow;
  shotNumber: number;
  title: string;
  context: string;
  timeSec: number | null;
  loop?: { enabled: boolean; aSec: number; bSec: number } | null;
};

type StatRow = {
  key: string;
  label: React.ReactNode;
  sortOrder: number;
  shot: string;
  attempts: number;
  failed: number;
  points: number;
  average: number | null;
  goal: number;
  goalAverage: number | null;
  pointsAdded: number;
};

type ComboRow = {
  key: string;
  first: React.ReactNode;
  firstSortOrder: number;
  firstTeam: Team01;
  second: React.ReactNode;
  secondSortOrder: number;
  attempts: number;
  failed: number;
  points: number;
  average: number | null;
  pointsAdded: number;
};

type KitchenRow = {
  key: string;
  label: React.ReactNode;
  attempts: number;
  wins: number;
  average: number | null;
  pointsAdded: number;
};

type SummaryRow = {
  playerId: PlayerId;
  sortOrder: number;
  total: number;
  raw: Record<string, number>;
  offense: number;
  defense: number;
};

type PointsAddedEvidenceItem = {
  id: string;
  playerId: PlayerId;
  team: Team01;
  kind: EvidenceKind;
  role: Role;
  row: KitchenArrivalRow;
  shotNumber: number;
  contribution: number;
  scored: boolean;
  goal: boolean;
  failed: boolean;
  formula?: PointsAddedShareFormula;
};

type PointsAddedShareFormula = {
  side: EvidenceKind;
  servingPoint: number;
  baseline: number;
  baselineServingPoints: number;
  baselineServeAttempts: number;
  delta: number;
  divisor: number;
  share: number;
};

type PointsAddedExportEvidenceItem = {
  id: string;
  playerId: PlayerId;
  playerLabel: string;
  kind: EvidenceKind;
  role: Role;
  roleLabel: string;
  rallyNumber: number;
  rallyShotCount: number;
  shotNumber: number;
  contribution: number;
  contributionText: string;
  outcome: string;
  scoreBefore: string;
  scoreAfter: string;
  videoUrl: string | null;
  timeSec: number | null;
  aSec: number | null;
  bSec: number | null;
  title: string;
  context: string;
  formula?: PointsAddedShareFormula | null;
};

type PointsAddedModel = {
  scoringRate: number;
  goalRate: number;
  teamPoints: Record<Team01, number>;
  finalScore: [number, number];
  offensive: StatRow[];
  defensive: StatRow[];
  firstThird: ComboRow[];
  thirdFifth: ComboRow[];
  secondFourth: ComboRow[];
  allOffensivePairs: ComboRow[];
  allDefensivePairs: ComboRow[];
  kitchen: KitchenRow[];
  summary: SummaryRow[];
  evidence: PointsAddedEvidenceItem[];
  pointRows: KitchenArrivalRow[];
  serveAttemptRows: KitchenArrivalRow[];
};

type PointsAddedPopupProps = {
  open: boolean;
  onClose: () => void;
  rows: KitchenArrivalRow[];
  packageId?: string | null;
  finalScore?: [number, number] | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  playerIdToTeam?: (playerId: number | null) => Team01 | null;
  cvJson?: CvJson | null;
  firefightsClips?: FirefightClip[];
  firefightRallySet?: Set<number>;
  variant?: "tables" | "text";
  onOpenShotTargetLab?: (row: KitchenArrivalRow, shotNumber: number) => void;
};

type PointsAddedShotPreview = {
  row: KitchenArrivalRow;
  shotNumber: number;
  title: string;
  context: string;
  timeSec: number | null;
  loop: { enabled: boolean; aSec: number; bSec: number; nonce: number } | null;
  nonce: number;
};

const PLAYER_IDS: PlayerId[] = [0, 1, 2, 3];
const LEGACY_ROLE_TO_SHOT: Record<string, number> = {
  serve: 1,
  return: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
};

const PROBABILITY_ROWS = [
  [0.01, "52.85%", "0.3351", "54.3%"],
  [0.02, "55.74%", "0.6714", "58.6%"],
  [0.03, "58.61%", "1.0103", "62.8%"],
  [0.04, "61.36%", "1.3402", "66.7%"],
  [0.05, "64.19%", "1.6766", "70.7%"],
  [0.06, "66.85%", "2.0039", "74.3%"],
  [0.07, "69.39%", "2.3247", "77.6%"],
  [0.08, "71.86%", "2.6347", "80.7%"],
  [0.09, "74.30%", "2.9472", "83.6%"],
  [0.1, "76.49%", "3.2425", "86.0%"],
  [0.11, "78.77%", "3.5565", "88.4%"],
  [0.12, "80.74%", "3.8403", "90.3%"],
  [0.13, "82.72%", "4.1241", "92.1%"],
  [0.14, "84.55%", "4.4028", "93.6%"],
  [0.15, "86.10%", "4.6552", "94.7%"],
  [0.2, "92.75%", "5.8774", "98.5%"],
  [0.25, "96.60%", "6.8872", "99.7%"],
  [0.3, "98.66%", "7.7255", "99.9%"],
  [0.35, "99.53%", "8.3969", "100.0%"],
] as const;

const POINTS_ADDED_DIALOG_TITLE_ID = "points-added-popup-title";

const PointsAddedDraggablePaper = React.forwardRef<HTMLDivElement, PaperProps>(
  function PointsAddedDraggablePaper(props, ref) {
    const nodeRef = React.useRef<HTMLDivElement>(null);

    const setRefs = (el: HTMLDivElement | null) => {
      nodeRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    };

    return (
      <Draggable
        nodeRef={nodeRef}
        handle={`#${POINTS_ADDED_DIALOG_TITLE_ID}`}
        cancel={
          'input,textarea,button,select,[role="button"],[class*="MuiDialogContent-root"],video'
        }
      >
        <Paper ref={setRefs} {...props} />
      </Draggable>
    );
  },
);

function collectInlineStylesForExport(): string {
  const styleText = Array.from(document.querySelectorAll("style"))
    .map((node) => node.textContent ?? "")
    .filter(Boolean)
    .join("\n")
    .replace(/\/\*# sourceMappingURL=.*?\*\//gs, "")
    .replace(/\/\*# sourceURL=.*?\*\//gs, "");
  return `<style>${styleText}</style>`;
}

function sanitizeHtmlForExport(html: string): string {
  return html.replace(/\s(src|href)=["']\s*["']/gi, "");
}

function safeFilenamePart(value: string | null | undefined): string {
  return (value || "points_added_report")
    .trim()
    .replace(/[^a-z0-9_-]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 96) || "points_added_report";
}

function downloadTextFile(filename: string, text: string, mimeType: string): void {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function jsonForInlineScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read video blob"));
    reader.readAsDataURL(blob);
  });
}

async function fetchVideoDataUrls(videoUrls: string[]): Promise<{
  videos: Record<string, string>;
  errors: Record<string, string>;
}> {
  const videos: Record<string, string> = {};
  const errors: Record<string, string> = {};

  for (const videoUrl of videoUrls) {
    try {
      if (videoUrl.startsWith("data:")) {
        videos[videoUrl] = videoUrl;
        continue;
      }
      const response = await fetch(videoUrl, { credentials: "same-origin" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      videos[videoUrl] = await blobToDataUrl(await response.blob());
    } catch (error) {
      errors[videoUrl] =
        error instanceof Error ? error.message : "Unknown video export error";
    }
  }

  return { videos, errors };
}

function pointsAddedExportRuntimeScript(): string {
  return `
(function () {
  var report = window.POINTS_ADDED_REPORT_DATA || {};
  var loadedVideoData = null;
  var videoMode = report.videoMode || "embedded";
  var blobUrlCache = {};
  var evidence = Array.isArray(report.evidence) ? report.evidence : [];

  function readInlineVideoData() {
    var node = document.getElementById("points-added-video-data-json");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (error) {
      return { videos: {}, errors: { inlineVideoData: "Could not parse embedded video data." } };
    }
  }

  function getVideoData() {
    if (!loadedVideoData) {
      loadedVideoData = window.POINTS_ADDED_VIDEO_DATA || readInlineVideoData() || { videos: {}, errors: {} };
      if (!report.videoMode && loadedVideoData.videoOmitted) {
        videoMode = "none";
      }
    }
    return loadedVideoData;
  }

  function dataUrlToBlobUrl(src, key) {
    if (!src || src.indexOf("data:") !== 0 || !window.URL || !window.Blob || !window.atob) {
      return src;
    }
    var cacheKey = key || src.slice(0, 96);
    if (blobUrlCache[cacheKey]) return blobUrlCache[cacheKey];
    try {
      var comma = src.indexOf(",");
      if (comma < 0) return src;
      var header = src.slice(0, comma);
      var mimeMatch = header.match(/^data:([^;,]+)/);
      var mime = mimeMatch ? mimeMatch[1] : "video/mp4";
      var binary = window.atob(src.slice(comma + 1));
      var bytes = new Uint8Array(binary.length);
      for (var i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      var blobUrl = window.URL.createObjectURL(new Blob([bytes], { type: mime }));
      blobUrlCache[cacheKey] = blobUrl;
      return blobUrl;
    } catch (error) {
      return src;
    }
  }

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (char) {
      if (char === "&") return "&amp;";
      if (char === "<") return "&lt;";
      if (char === ">") return "&gt;";
      if (char === '"') return "&quot;";
      if (char === "'") return "&#39;";
      return char;
    });
  }

  function fmtExportNumber(value) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return Math.abs(n) >= 0.95 ? n.toFixed(2) : n.toFixed(2);
  }

  function fmtExportSigned(value) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "-";
    var abs = Math.abs(n);
    var text = abs.toFixed(2);
    return n < -0.005 ? "(" + text + ")" : "+" + text;
  }

  function pctExport(value) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return Math.round(n * 1000) / 10 + "%";
  }

  function contributionMathHtml(item) {
    var formula = item && item.formula;
    if (!formula) return "";
    var isCreated = formula.side === "created";
    var firstValue = isCreated ? formula.servingPoint : formula.baseline;
    var secondValue = isCreated ? formula.baseline : formula.servingPoint;
    var firstLabel = isCreated ? "server result" : "expected chance of winning rally";
    var secondLabel = isCreated ? "expected chance of winning rally" : "server result";
    var sideLabel = isCreated ? "serving side" : "receiving side";
    var resultText = Number(formula.servingPoint) > 0.5 ? "server scored" : "server did not score";
    var baselineCountText = Number.isFinite(Number(formula.baselineServingPoints)) &&
      Number.isFinite(Number(formula.baselineServeAttempts)) &&
      Number(formula.baselineServeAttempts) > 0
        ? esc(formula.baselineServingPoints) + ' of ' + esc(formula.baselineServeAttempts) + ' service rallies'
        : '';
    var touchText = Number(formula.divisor) + " " +
      (isCreated ? "serving-team" : "receiving-team") +
      " tagged " + (Number(formula.divisor) === 1 ? "touch" : "touches");
    return '' +
      '<details class="pa-export-math">' +
        '<summary>calculation</summary>' +
        '<div><b>Server result:</b> ' + esc(resultText) + ' (' + esc(fmtExportNumber(formula.servingPoint)) + ').</div>' +
        '<div><b>Expected chance of winning rally:</b> ' + esc(pctExport(formula.baseline)) +
          (baselineCountText ? ' because the serving side scored on ' + baselineCountText + ' across both teams.' : '.') + '</div>' +
        '<div><b>Rally value for the ' + esc(sideLabel) + ':</b> ' + esc(fmtExportNumber(firstValue)) +
          ' - ' + esc(fmtExportNumber(secondValue)) + ' = ' + esc(fmtExportSigned(formula.delta)) + '.</div>' +
        '<div>This contribution is split across ' + esc(touchText) + ', so this row gets ' + esc(fmtExportSigned(formula.share)) + '.</div>' +
        '<div><b>contribution to total</b> = (' + esc(firstLabel) + ' - ' + esc(secondLabel) + ') / touches</div>' +
        '<div>= (' + esc(fmtExportNumber(firstValue)) + ' - ' + esc(fmtExportNumber(secondValue)) + ') / ' +
          esc(formula.divisor) + ' = ' + esc(fmtExportSigned(formula.delta)) + ' / ' +
          esc(formula.divisor) + ' = <b>' + esc(fmtExportSigned(formula.share)) + '</b></div>' +
        '<div>Table rounds this contribution to ' + esc(item.contributionText) + '.</div>' +
      '</details>';
  }

  function matchExpectationHtml(items) {
    var formulaItem = (items || []).find(function (item) { return item && item.formula; });
    if (!formulaItem || !formulaItem.formula) return "";
    var formula = formulaItem.formula;
    var countText = Number.isFinite(Number(formula.baselineServingPoints)) &&
      Number.isFinite(Number(formula.baselineServeAttempts)) &&
      Number(formula.baselineServeAttempts) > 0
        ? ' In this report that is <b>' + esc(formula.baselineServingPoints) + '</b> scoring service rallies divided by <b>' + esc(formula.baselineServeAttempts) + '</b> total service rallies.'
        : '';
    return '' +
      '<details class="pa-export-philosophy">' +
        '<summary>What is expected chance of winning rally?</summary>' +
        '<p><b>Expected chance of winning rally</b> is this game\\'s combined serving-team scoring rate across both teams. It pools Team 1\\'s service rallies and Team 2\\'s service rallies, then asks how often the serving side scored in this particular game. It is the report\\'s in-game comparison point, not a universal pickleball average. Here that comparison point is ' +
          esc(pctExport(formula.baseline)) + '.' + countText + '</p>' +
        '<p>It is used as a neutral in-game bar: when the serving team scores, the serving-side touches get credit for beating that bar; when the serving team does not score, the receiving-side touches get credit for holding the server below that bar. The rally contribution is then split across the tagged touches for that side.</p>' +
        '<p>The original Mathlete-style examples often compare rows to a broader expected scoring number, such as a historical pickleball baseline. That answers, "was this above a general expectation?" This top summary instead asks, "inside this game, which players and touches tilted rallies away from this game\\'s match pattern?"</p>' +
        '<p>Tradeoff: this keeps both teams on the same scoreboard-tied scale, but it is not an absolute skill rating. If both teams played unusually well or unusually poorly, this in-game bar moves with them. A historical benchmark can still be a useful second lens later.</p>' +
      '</details>';
  }

  function closePopup(name) {
    document.querySelectorAll('[data-pa-export-popup="' + name + '"]').forEach(function (node) {
      node.remove();
    });
  }

  function makeDraggable(popup, handle) {
    handle.addEventListener("mousedown", function (event) {
      if (event.button !== 0) return;
      event.preventDefault();
      var rect = popup.getBoundingClientRect();
      var startX = event.clientX;
      var startY = event.clientY;
      var originX = rect.left;
      var originY = rect.top;

      function onMove(moveEvent) {
        var nextX = Math.max(8, Math.min(window.innerWidth - 220, originX + moveEvent.clientX - startX));
        var nextY = Math.max(8, Math.min(window.innerHeight - 120, originY + moveEvent.clientY - startY));
        popup.style.left = nextX + "px";
        popup.style.top = nextY + "px";
      }

      function onUp() {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      }

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    });
  }

  function openPopup(name, title, subtitle, bodyHtml, options) {
    closePopup(name);
    var opts = options || {};
    var popup = document.createElement("div");
    popup.className = "pa-export-popup";
    popup.setAttribute("data-pa-export-popup", name);
    popup.style.left = (opts.left || 80) + "px";
    popup.style.top = (opts.top || 80) + "px";
    popup.style.width = opts.width || "min(900px, calc(100vw - 32px))";
    popup.innerHTML =
      '<div class="pa-export-popup-header">' +
        '<div class="pa-export-popup-title-wrap">' +
          '<div class="pa-export-popup-title">' + esc(title) + '</div>' +
          '<div class="pa-export-popup-subtitle">' + esc(subtitle || "") + '</div>' +
        '</div>' +
        '<button type="button" class="pa-export-popup-close" aria-label="Close">x</button>' +
      '</div>' +
      '<div class="pa-export-popup-body">' + bodyHtml + '</div>';
    document.body.appendChild(popup);
    popup.querySelector(".pa-export-popup-close").addEventListener("click", function () {
      closePopup(name);
    });
    makeDraggable(popup, popup.querySelector(".pa-export-popup-header"));
    return popup;
  }

  function itemsFor(playerId, kind) {
    return evidence
      .filter(function (item) {
        return Number(item.playerId) === Number(playerId) && item.kind === kind;
      })
      .sort(function (a, b) {
        var aPositive = Number(a.contribution || 0) > 0.05 ? 0 : 1;
        var bPositive = Number(b.contribution || 0) > 0.05 ? 0 : 1;
        if (aPositive !== bPositive) return aPositive - bPositive;
        return Number(a.rallyNumber || 0) - Number(b.rallyNumber || 0) ||
          Number(a.shotNumber || 0) - Number(b.shotNumber || 0);
      });
  }

  function openEvidence(playerId, kind) {
    var items = itemsFor(playerId, kind);
    var playerLabel = items[0] ? items[0].playerLabel : "p" + playerId;
    var label = kind === "created" ? "Serving impact" : "Defending impact";
    var rowsHtml = items.length
      ? items.map(function (item) {
          var good = Number(item.contribution || 0) >= 0;
          return '<tr class="' + (good ? "pa-good" : "pa-bad") + '">' +
            '<td>' + esc(item.rallyNumber) + '</td>' +
            '<td>' + esc(item.rallyShotCount) + '</td>' +
            '<td>' + esc(item.shotNumber) + '</td>' +
            '<td>' + esc(item.roleLabel) + '</td>' +
            '<td>' + esc(item.scoreBefore) + ' -> ' + esc(item.scoreAfter) + '</td>' +
            '<td>' + esc(item.outcome) + '</td>' +
            '<td><b>' + esc(item.contributionText) + '</b>' + contributionMathHtml(item) + '</td>' +
            '<td><button type="button" class="pa-export-watch" data-pa-watch-id="' + esc(item.id) + '">Watch</button></td>' +
          '</tr>';
        }).join("")
      : '<tr><td colspan="8">No evidence rows for this player yet.</td></tr>';
    var popup = openPopup(
      "evidence",
      playerLabel + " · " + label,
      "Exported evidence clips. Green rows helped; red rows are review candidates.",
      matchExpectationHtml(items) +
      '<table class="pa-export-evidence-table">' +
        '<thead><tr><th>Rally</th><th>Rally shots</th><th>Shot</th><th>Role</th><th>Score</th><th>Outcome</th><th>Contribution to total</th><th>Video</th></tr></thead>' +
        '<tbody>' + rowsHtml + '</tbody>' +
      '</table>',
      { left: 70, top: 70, width: "min(980px, calc(100vw - 32px))" }
    );
    popup.querySelectorAll("[data-pa-watch-id]").forEach(function (button) {
      button.addEventListener("click", function () {
        openVideo(button.getAttribute("data-pa-watch-id"));
      });
    });
  }

  function openVideo(id) {
    var item = evidence.find(function (candidate) { return candidate.id === id; });
    if (!item) return;
    var videoData = getVideoData();
    var bodyHtml = "";
    if (videoData.debugVideoJunk) {
      bodyHtml =
        '<div class="pa-export-video-missing">' +
          '<b>Debug-with-video export.</b><br />' +
          'This file keeps the video wiring and evidence buttons, but intentionally omits every clip payload so the HTML can be inspected or sent around without a huge embedded video blob.' +
        '</div>';
    } else {
      var src = item.videoUrl ? videoData.videos[item.videoUrl] : null;
      var error = item.videoUrl ? videoData.errors[item.videoUrl] : null;
      var playbackSrc = src ? dataUrlToBlobUrl(src, item.videoUrl || item.id) : null;
      if (playbackSrc) {
        bodyHtml =
          '<video class="pa-export-video" controls playsinline preload="metadata" src="' + esc(playbackSrc) + '"></video>' +
          '<div class="pa-export-video-actions">' +
            '<button type="button" data-pa-replay>Replay clip window</button>' +
            '<span>Clip window: ' + esc((item.aSec == null ? item.timeSec : item.aSec) == null ? "-" : Number(item.aSec == null ? item.timeSec : item.aSec).toFixed(2) + "s") +
            ' to ' + esc(item.bSec == null ? "-" : Number(item.bSec).toFixed(2) + "s") + '</span>' +
          '</div>';
      } else {
        if (videoMode === "none") {
          bodyHtml =
            '<div class="pa-export-video-missing">' +
              '<b>This is the no-video export.</b><br />' +
              'The evidence row is preserved, but embedded clip data was intentionally omitted so this HTML stays small and opens cleanly from <code>file://</code>. Open the <code>_with_video.html</code> export when you want the Watch buttons to play clips.' +
            '</div>';
        } else {
          bodyHtml =
            '<div class="pa-export-video-missing">' +
              '<b>Video data is not available for this clip.</b><br />' +
              'This export should contain embedded video data, so try exporting the video version again from the app.' +
              (error ? '<br /><br />Fetch error: ' + esc(error) : "") +
            '</div>';
        }
      }
    }

    var popup = openPopup(
      "video",
      item.title || ("Rally " + item.rallyNumber + " · Shot " + item.shotNumber),
      item.context || "",
      bodyHtml,
      { left: 120, top: 96, width: "min(860px, calc(100vw - 32px))" }
    );
    var video = popup.querySelector("video");
    if (!video) return;
    var start = Math.max(0, Number(item.aSec == null ? item.timeSec || 0 : item.aSec));
    var end = item.bSec == null ? null : Number(item.bSec);
    var started = false;
    function seekStart() {
      try {
        video.currentTime = start;
      } catch (error) {
        // Some browsers reject early currentTime writes until metadata is ready.
      }
    }
    function playClip() {
      video.play().catch(function () {});
    }
    function loopEnd() {
      if (end == null) return null;
      var duration = Number(video.duration);
      if (Number.isFinite(duration) && duration > 0) {
        return Math.min(end, Math.max(start + 0.25, duration - 0.05));
      }
      return end;
    }
    function loopToStart() {
      seekStart();
      playClip();
    }
    video.autoplay = true;
    video.addEventListener("loadedmetadata", function () {
      loopToStart();
    });
    video.addEventListener("canplay", function () {
      if (!started) {
        started = true;
        loopToStart();
      }
    });
    video.addEventListener("timeupdate", function () {
      var clippedEnd = loopEnd();
      if (clippedEnd != null && video.currentTime >= clippedEnd) {
        loopToStart();
      }
    });
    video.addEventListener("ended", loopToStart);
    popup.querySelector("[data-pa-replay]")?.addEventListener("click", function () {
      loopToStart();
    });
    window.setTimeout(function () {
      loopToStart();
    }, 0);
  }

  function setDefinitionsOpen(open) {
    document.querySelectorAll("[data-pa-definitions-region]").forEach(function (region) {
      region.style.display = open ? "block" : "none";
    });
    document.querySelectorAll("[data-pa-definitions-toggle]").forEach(function (button) {
      button.textContent = open ? "Hide definitions" : "Show definitions";
      button.setAttribute("data-pa-definitions-open", open ? "true" : "false");
    });
  }

  function attachEvidenceButtons() {
    document.querySelectorAll("[data-pa-definitions-toggle]").forEach(function (button) {
      var initialOpen = button.getAttribute("data-pa-definitions-open") === "true";
      setDefinitionsOpen(initialOpen);
      button.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        setDefinitionsOpen(button.getAttribute("data-pa-definitions-open") !== "true");
      });
    });
    document.querySelectorAll("[data-pa-evidence-player][data-pa-evidence-kind]").forEach(function (button) {
      button.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        openEvidence(button.getAttribute("data-pa-evidence-player"), button.getAttribute("data-pa-evidence-kind"));
      });
    });
    document.querySelectorAll("[data-pa-watch-id]").forEach(function (button) {
      button.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        openVideo(button.getAttribute("data-pa-watch-id"));
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachEvidenceButtons);
  } else {
    attachEvidenceButtons();
  }
})();
`;
}

function asPlayerId(value: number | null | undefined): PlayerId | null {
  if (value === 0 || value === 1 || value === 2 || value === 3) return value;
  return null;
}

function teamOfPlayer(playerId: PlayerId): Team01 {
  return playerId < 2 ? 0 : 1;
}

function otherTeam(team: Team01): Team01 {
  return team === 0 ? 1 : 0;
}

function shot(row: KitchenArrivalRow, shotNumber: number): ShotNumberOverlay | null {
  return (
    row.shotNumberOverlays?.find((overlay) => overlay.shotNumber === shotNumber) ??
    null
  );
}

function pointTeam(row: KitchenArrivalRow): Team01 | null {
  const before = row.scoreBefore;
  const after = row.scoreAfter;
  if (Array.isArray(before) && Array.isArray(after)) {
    const team0Delta = Number(after[0] ?? 0) - Number(before[0] ?? 0);
    const team1Delta = Number(after[1] ?? 0) - Number(before[1] ?? 0);
    if (team0Delta === 1 && team1Delta === 0) return 0;
    if (team1Delta === 1 && team0Delta === 0) return 1;
    if (team0Delta === 0 && team1Delta === 0) return null;
  }

  if (row.pointAwardedTeam === 0 || row.pointAwardedTeam === 1) {
    return row.pointAwardedTeam;
  }
  return null;
}

function rowPointForTeam(row: KitchenArrivalRow, team: Team01): boolean {
  return pointTeam(row) === team;
}

function servingGoal(row: KitchenArrivalRow): boolean {
  return pointTeam(row) === row.servingTeam || row.servingArrivalSuccess === true;
}

function pct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return `${Math.round(value * 1000) / 10}%`;
}

function fmtNumber(value: number): string {
  if (!Number.isFinite(value)) return "-";
  const rounded = Math.round(value * 10) / 10;
  return rounded.toFixed(1);
}

function fmtContributionNumber(value: number): string {
  if (!Number.isFinite(value)) return "-";
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(2);
}

function fmtSigned(value: number): string {
  if (!Number.isFinite(value)) return "-";
  if (value > 0.05) return `+${fmtNumber(value)}`;
  if (value < -0.05) return `(${fmtNumber(Math.abs(value))})`;
  return "0.0";
}

function fmtContributionSigned(value: number): string {
  if (!Number.isFinite(value)) return "-";
  if (value > 0.005) return `+${fmtContributionNumber(value)}`;
  if (value < -0.005) return `(${fmtContributionNumber(Math.abs(value))})`;
  return "0.00";
}

function scorePairText(score: unknown): string {
  if (!Array.isArray(score) || score.length < 2) return "-";
  return `${Number(score[0] ?? 0)}-${Number(score[1] ?? 0)}`;
}

function isPointRow(row: KitchenArrivalRow): boolean {
  return pointTeam(row) != null && (row.servingTeam === 0 || row.servingTeam === 1);
}

function isServeAttemptRow(row: KitchenArrivalRow): boolean {
  return row.servingTeam === 0 || row.servingTeam === 1;
}

function playerForShot(row: KitchenArrivalRow, shotNumber: number): PlayerId | null {
  return asPlayerId(shot(row, shotNumber)?.playerId);
}

function ordinal(value: number): string {
  const abs = Math.abs(Math.trunc(value));
  const teen = abs % 100;
  if (teen >= 11 && teen <= 13) return `${value}th`;
  switch (abs % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

function roleFromShotNumber(shotNumber: number): Role {
  return `shot:${Math.max(1, Math.trunc(shotNumber))}`;
}

function roleShotNumber(role: Role): number {
  const match = /^shot:(\d+)$/.exec(role);
  if (match) return Number(match[1]);
  return LEGACY_ROLE_TO_SHOT[role] ?? 5;
}

function shotNumberLabel(shotNumber: number): string {
  if (shotNumber === 1) return "Serves";
  if (shotNumber === 2) return "Returns";
  return `${ordinal(shotNumber)} Shots`;
}

function roleLabel(role: Role): string {
  return shotNumberLabel(roleShotNumber(role));
}

function roleSummaryLabel(role: Role): string {
  const shotNumber = roleShotNumber(role);
  if (shotNumber === 1) return "serve";
  if (shotNumber === 2) return "return defense";
  return shotNumber % 2 === 1
    ? `${ordinal(shotNumber)} shot`
    : `${ordinal(shotNumber)}-shot defense`;
}

function isCreatedRole(role: Role): boolean {
  return roleShotNumber(role) % 2 === 1;
}

function isPreventedRole(role: Role): boolean {
  return roleShotNumber(role) % 2 === 0;
}

function roleRank(role: Role): number {
  return roleShotNumber(role);
}

function rawValue(raw: Record<string, number>, role: Role): number {
  return raw[role] ?? 0;
}

function rawAverageForRoles(
  raw: Record<string, number>,
  predicate: (role: Role) => boolean,
): number {
  const roles = Object.keys(raw).filter(predicate);
  if (!roles.length) return 0;
  return roles.reduce((sum, role) => sum + rawValue(raw, role), 0) / roles.length;
}

function sortedRolesFromSummaries(rows: Array<{ raw: Record<string, number> }>): Role[] {
  return Array.from(new Set(rows.flatMap((row) => Object.keys(row.raw)))).sort(
    (a, b) => roleRank(a) - roleRank(b),
  );
}

function rowShotNumbers(row: KitchenArrivalRow): number[] {
  return Array.from(
    new Set(
      (row.shotNumberOverlays ?? [])
        .map((overlay) => Number(overlay.shotNumber))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
  ).sort((a, b) => a - b);
}

function rowShotCount(row: KitchenArrivalRow): number {
  return rowShotNumbers(row).length;
}

function faultFlagPresent(value: unknown): boolean {
  if (value === true) return true;
  if (value && typeof value === "object") return true;
  return false;
}

function shotMissed(overlay: ShotNumberOverlay | null): boolean {
  if (!overlay) return false;
  if (typeof overlay.faultType === "string" && overlay.faultType.length > 0) {
    return true;
  }

  const faults = (overlay.rawShot as any)?.errors?.faults;
  if (!faults || typeof faults !== "object") return false;
  return (
    faultFlagPresent(faults.net) ||
    faultFlagPresent(faults.out) ||
    faultFlagPresent(faults.short) ||
    faultFlagPresent(faults.kitchen) ||
    faultFlagPresent(faults.excess_bounce) ||
    faultFlagPresent(faults.paddle_hit_net)
  );
}

function terminalMissedShotNumber(row: KitchenArrivalRow): number | null {
  const terminal =
    [...(row.shotNumberOverlays ?? [])].sort(
      (a, b) => Number(b.shotNumber ?? 0) - Number(a.shotNumber ?? 0),
    )[0] ?? null;
  if (!terminal || !shotMissed(terminal)) return null;
  return terminal.shotNumber;
}

function terminalShotMissed(
  row: KitchenArrivalRow,
  shotNumber: number,
): boolean {
  return terminalMissedShotNumber(row) === shotNumber;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function rowSegmentEndMsRel(row: KitchenArrivalRow): number | null {
  const startMs = finiteNumber(row.segmentStartMs ?? row.startMs);
  const endMs = finiteNumber(row.segmentEndMs ?? row.endMs);
  if (startMs == null || endMs == null || endMs <= startMs) return null;
  return endMs - startMs;
}

function normalizeOverlayMsRel(
  value: unknown,
  row: KitchenArrivalRow,
): number | null {
  const t = finiteNumber(value);
  if (t == null) return null;

  const segmentStartMs = finiteNumber(row.segmentStartMs ?? row.startMs);
  const segmentEndMs = finiteNumber(row.segmentEndMs ?? row.endMs);
  const segmentDurationMs =
    segmentStartMs != null && segmentEndMs != null && segmentEndMs > segmentStartMs
      ? segmentEndMs - segmentStartMs
      : null;

  if (
    segmentStartMs != null &&
    ((segmentDurationMs != null && t > segmentDurationMs * 1.5) ||
      t >= segmentStartMs - 1500)
  ) {
    return Math.max(0, t - segmentStartMs);
  }

  return Math.max(0, t);
}

function overlayMsRel(
  row: KitchenArrivalRow,
  overlay: ShotNumberOverlay | null,
): number | null {
  if (!overlay) return null;
  const rawShot = (overlay.rawShot ?? {}) as any;
  const candidates = [
    overlay.atMsRel,
    overlay.fullFromMsRel,
    overlay.showFromMsRel,
    overlay.fadeStartMsRel,
    overlay.hideAtMsRel,
    (overlay as any).trajectoryStart?.ms,
    (overlay as any).trajectoryEnd?.ms,
    rawShot.atMsRel,
    rawShot.msRel,
    rawShot.startMsRel,
    rawShot.contactMsRel,
    rawShot.at_ms_rel,
    rawShot.start_ms_rel,
    rawShot.contact_ms_rel,
    rawShot.ms,
    rawShot.startMs,
    rawShot.contactMs,
    rawShot.at_ms,
    rawShot.start_ms,
    rawShot.contact_ms,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeOverlayMsRel(candidate, row);
    if (normalized != null) return normalized;
  }

  return null;
}

function orderedOverlays(row: KitchenArrivalRow): ShotNumberOverlay[] {
  return [...(row.shotNumberOverlays ?? [])].sort((a, b) => {
    const shotDelta = Number(a.shotNumber ?? 0) - Number(b.shotNumber ?? 0);
    if (shotDelta !== 0) return shotDelta;
    return (overlayMsRel(row, a) ?? 0) - (overlayMsRel(row, b) ?? 0);
  });
}

function inferredShotTimings(row: KitchenArrivalRow): Array<{
  overlay: ShotNumberOverlay;
  msRel: number;
}> {
  const overlays = orderedOverlays(row);
  if (!overlays.length) return [];

  const segmentDurationMs =
    rowSegmentEndMsRel(row) ??
    Math.max(
      1200,
      (finiteNumber(row.endMs) ?? 0) - (finiteNumber(row.startMs) ?? 0),
      overlays.length * 900,
    );

  const raw = overlays.map((overlay) => overlayMsRel(row, overlay));
  const usableRaw = raw.filter((value): value is number => value != null);
  const minRaw = usableRaw.length ? Math.min(...usableRaw) : null;
  const maxRaw = usableRaw.length ? Math.max(...usableRaw) : null;
  const rawLooksDistinct =
    usableRaw.length >= Math.min(2, overlays.length) &&
    minRaw != null &&
    maxRaw != null &&
    maxRaw - minRaw >= Math.max(200, overlays.length * 90);

  if (rawLooksDistinct) {
    return overlays
      .map((overlay, index) => ({
        overlay,
        msRel:
          raw[index] ??
          ((index + 1) * segmentDurationMs) / Math.max(2, overlays.length + 1),
      }))
      .sort((a, b) => a.msRel - b.msRel || a.overlay.shotNumber - b.overlay.shotNumber);
  }

  const spacing = segmentDurationMs / Math.max(2, overlays.length + 1);
  return overlays.map((overlay, index) => ({
    overlay,
    msRel: Math.max(0, Math.min(segmentDurationMs, spacing * (index + 1))),
  }));
}

function inferredShotMsRel(
  row: KitchenArrivalRow,
  shotNumber: number,
): number | null {
  const match = inferredShotTimings(row).find(
    (entry) => entry.overlay.shotNumber === shotNumber,
  );
  return match?.msRel ?? null;
}

function rowWithPreviewShotTiming(row: KitchenArrivalRow): KitchenArrivalRow {
  const timings = inferredShotTimings(row);
  if (!timings.length) return row;

  const segmentDurationMs =
    rowSegmentEndMsRel(row) ??
    Math.max(timings[timings.length - 1]?.msRel ?? 0, 1500);
  const timingByShot = new Map(
    timings.map((entry, index) => [
      entry.overlay.shotNumber,
      {
        msRel: entry.msRel,
        nextMsRel: timings[index + 1]?.msRel ?? segmentDurationMs + 700,
      },
    ]),
  );

  return {
    ...row,
    shotNumberOverlays: (row.shotNumberOverlays ?? []).map((overlay) => {
      const timing = timingByShot.get(overlay.shotNumber);
      if (!timing) return overlay;

      const showFromMsRel = Math.max(0, timing.msRel - 500);
      const fullFromMsRel = Math.max(0, timing.msRel - 120);
      const fadeStartMsRel = Math.max(
        timing.msRel + 550,
        Math.min(timing.nextMsRel - 120, timing.msRel + 1100),
      );
      const hideAtMsRel = Math.max(fadeStartMsRel + 240, timing.nextMsRel + 160);

      return {
        ...overlay,
        atMsRel: timing.msRel,
        showFromMsRel,
        fullFromMsRel,
        fadeStartMsRel,
        hideAtMsRel,
      };
    }),
  };
}

function shotLoopForRow(
  row: KitchenArrivalRow,
  shotNumber: number,
  missedShotNumber: number | null,
): { timeSec: number | null; loop: { enabled: boolean; aSec: number; bSec: number } | null } {
  const timings = inferredShotTimings(row);
  const currentEntry = timings.find(
    (entry) => entry.overlay.shotNumber === shotNumber,
  );
  const currentMs = currentEntry?.msRel ?? inferredShotMsRel(row, shotNumber);
  if (currentMs == null) return { timeSec: null, loop: null };

  const index = timings.findIndex(
    (entry) => entry.overlay.shotNumber === shotNumber,
  );
  const nextMs = index >= 0 ? timings[index + 1]?.msRel : null;
  const rallyEndMs = rowSegmentEndMsRel(row);
  const contextualEndMs =
    rallyEndMs != null && rallyEndMs > currentMs
      ? rallyEndMs
      : null;
  const endMs =
    contextualEndMs ??
    (missedShotNumber === shotNumber
      ? rowSegmentEndMsRel(row) ?? nextMs ?? currentMs + 1800
      : nextMs ?? rowSegmentEndMsRel(row) ?? currentMs + 1800);
  const aSec = Math.max(0, currentMs / 1000 - 0.55);
  const bSec = Math.max(aSec + 0.5, endMs / 1000 + 0.35);
  return {
    timeSec: Math.max(0, currentMs / 1000 - 0.05),
    loop: { enabled: true, aSec, bSec },
  };
}

function buildPointsAddedExportEvidence(
  model: PointsAddedModel,
  playerText: (playerId: PlayerId) => string,
): PointsAddedExportEvidenceItem[] {
  return model.evidence.map((item) => {
    const timing = shotLoopForRow(
      item.row,
      item.shotNumber,
      terminalMissedShotNumber(item.row),
    );
    const videoUrl =
      typeof item.row.videoUrl === "string" && item.row.videoUrl.length > 0
        ? item.row.videoUrl
        : null;
    const title = `Rally ${item.row.rallyIndex + 1} · Shot ${item.shotNumber}`;
    const context = `${roleLabel(item.role)} by ${playerText(item.playerId)} · ${evidenceKindLabel(
      item.kind,
    )} ${fmtContributionSigned(item.contribution)} · ${evidenceOutcomeText(item)}`;

    return {
      id: item.id,
      playerId: item.playerId,
      playerLabel: playerText(item.playerId),
      kind: item.kind,
      role: item.role,
      roleLabel: roleLabel(item.role),
      rallyNumber: item.row.rallyIndex + 1,
      rallyShotCount: rowShotCount(item.row),
      shotNumber: item.shotNumber,
      contribution: item.contribution,
      contributionText: fmtContributionSigned(item.contribution),
      outcome: evidenceOutcomeText(item),
      scoreBefore: scorePairText(item.row.scoreBefore),
      scoreAfter: scorePairText(item.row.scoreAfter),
      videoUrl,
      timeSec: timing.timeSec,
      aSec: timing.loop?.aSec ?? timing.timeSec,
      bSec: timing.loop?.bSec ?? null,
      title,
      context,
      formula: item.formula ?? null,
    };
  });
}

function addStat(map: Map<string, StatRow>, key: string, patch: Omit<StatRow, "key">) {
  const current = map.get(key);
  if (!current) {
    map.set(key, { key, ...patch });
    return;
  }
  current.attempts += patch.attempts;
  current.failed += patch.failed;
  current.points += patch.points;
  current.goal += patch.goal;
  current.pointsAdded += patch.pointsAdded;
  current.average =
    current.attempts > 0 ? current.points / current.attempts : null;
  current.goalAverage =
    current.attempts > 0 ? current.goal / current.attempts : null;
}

function addCombo(map: Map<string, ComboRow>, row: ComboRow) {
  const current = map.get(row.key);
  if (!current) {
    map.set(row.key, row);
    return;
  }
  current.attempts += row.attempts;
  current.failed += row.failed;
  current.points += row.points;
  current.pointsAdded += row.pointsAdded;
  current.average =
    current.attempts > 0 ? current.points / current.attempts : null;
}

function addKitchen(map: Map<string, KitchenRow>, row: KitchenRow) {
  const current = map.get(row.key);
  if (!current) {
    map.set(row.key, row);
    return;
  }
  current.attempts += row.attempts;
  current.wins += row.wins;
  current.pointsAdded += row.pointsAdded;
  current.average = current.attempts > 0 ? current.wins / current.attempts : null;
}

export function PointsAddedPopup(props: PointsAddedPopupProps) {
  const {
    open,
    onClose,
    rows,
    packageId,
    finalScore,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    playerIdToTeam,
    cvJson,
    firefightsClips = [],
    firefightRallySet = new Set<number>(),
    variant = "tables",
    onOpenShotTargetLab,
  } = props;

  const playerAvatar = React.useCallback(
    (playerId: number | null | undefined, size = 24) => {
      const parsed = asPlayerId(playerId);
      if (parsed == null) return <span style={{ opacity: 0.42 }}>-</span>;
      return renderPlayerIdentity({
        playerId: parsed,
        size,
        fontSize: Math.max(9, Math.floor(size * 0.44)),
        borderRadius: 6,
        avatarOnly: true,
      });
    },
    [renderPlayerIdentity],
  );

  const playerBadge = React.useCallback(
    (playerId: number | null | undefined, size = 32, missed = false) => {
      const parsed = asPlayerId(playerId);
      if (parsed == null) return <span style={{ opacity: 0.42 }}>-</span>;
      return (
        <Box
          title={`PBV player p${parsed}`}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}
        >
          <Box component="span" sx={{ fontWeight: 900 }}>
            {`p${parsed}`}
          </Box>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            {playerAvatar(parsed, size)}
            {missed ? (
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  inset: -4,
                  display: "grid",
                  placeItems: "center",
                  color: "#d00000",
                  fontSize: Math.max(18, Math.floor(size * 0.92)),
                  fontWeight: 1000,
                  lineHeight: 1,
                  textShadow: "0 0 2px #fff, 0 0 2px #fff",
                  pointerEvents: "none",
                }}
              >
                X
              </Box>
            ) : null}
          </Box>
        </Box>
      );
    },
    [playerAvatar],
  );

  const playerText = React.useCallback(
    (playerId: PlayerId) => `p${playerId}`,
    [],
  );

  const playerSortOrder = React.useCallback(
    (playerId: PlayerId) => playerId,
    [],
  );

  const teamForPlayer = React.useCallback(
    (playerId: PlayerId): Team01 =>
      playerIdToTeam?.(playerId) ?? teamOfPlayer(playerId),
    [playerIdToTeam],
  );

  const teamPlayerIds = React.useCallback(
    (team: Team01): PlayerId[] => {
      const players = PLAYER_IDS.filter((playerId) => teamForPlayer(playerId) === team);
      return (players.length ? players : ([team === 0 ? 0 : 2, team === 0 ? 1 : 3] as PlayerId[])).sort(
        (a, b) => playerSortOrder(a) - playerSortOrder(b),
      );
    },
    [playerSortOrder, teamForPlayer],
  );

  const displayTeamText = React.useCallback(
    (team: Team01) => teamPlayerIds(team).map(playerText).join(" / "),
    [playerText, teamPlayerIds],
  );

  const teamBadge = React.useCallback(
    (team: Team01) => {
      const players = teamPlayerIds(team);
      return (
        <Box sx={{ display: "inline-flex", gap: 0.7, alignItems: "center" }}>
          <Box component="span" sx={{ fontWeight: 900 }}>
            {`Team ${team + 1},`}
          </Box>
          {players.map((playerId) => (
            <React.Fragment key={playerId}>{playerBadge(playerId, 32)}</React.Fragment>
          ))}
        </Box>
      );
    },
    [playerBadge, teamPlayerIds],
  );

  const model = React.useMemo<PointsAddedModel>(() => {
    const pointRows = rows.filter(isPointRow);
    const serveAttemptRows = rows.filter(isServeAttemptRow);
    const teamPoints: Record<Team01, number> = { 0: 0, 1: 0 };
    for (const row of pointRows) {
      const awarded = pointTeam(row);
      if (awarded === 0 || awarded === 1) teamPoints[awarded] += 1;
    }
    const scoringRate =
      serveAttemptRows.length > 0
        ? serveAttemptRows.filter((row) => pointTeam(row) === row.servingTeam)
            .length / serveAttemptRows.length
        : 0;
    const goalRows = serveAttemptRows;
    const goalRate =
      goalRows.length > 0
        ? goalRows.filter((row) => servingGoal(row)).length / goalRows.length
        : 0;

    const offensive = new Map<string, StatRow>();
    const defensive = new Map<string, StatRow>();
    const playerRaw: Record<PlayerId, Record<string, number>> = {
      0: {},
      1: {},
      2: {},
      3: {},
    };
    const evidence: PointsAddedEvidenceItem[] = [];

    const addOffensiveAttempt = (row: KitchenArrivalRow, shotNumber: number) => {
      const role = roleFromShotNumber(shotNumber);
      const playerId = playerForShot(row, shotNumber);
      if (playerId == null) return;
      const team = teamForPlayer(playerId);
      const points = rowPointForTeam(row, team) ? 1 : 0;
      const failed = terminalShotMissed(row, shotNumber) ? 1 : 0;
      const goal =
        points || (row.servingTeam === team && row.servingArrivalSuccess === true)
          ? 1
          : 0;
      const pointsAdded = points - scoringRate;
      playerRaw[playerId][role] = rawValue(playerRaw[playerId], role) + pointsAdded;
      evidence.push({
        id: `${row.rallyIndex}:${shotNumber}:${playerId}:created`,
        playerId,
        team,
        kind: "created",
        role,
        row,
        shotNumber,
        contribution: pointsAdded,
        scored: points > 0,
        goal: goal > 0,
        failed: failed > 0,
      });
      addStat(offensive, `p${playerId}:${role}`, {
        label: playerBadge(playerId),
        sortOrder: playerSortOrder(playerId),
        shot: roleLabel(role),
        attempts: 1,
        failed,
        points,
        average: points,
        goal,
        goalAverage: goal,
        pointsAdded,
      });
      addStat(offensive, `t${team}:${role}`, {
        label: teamBadge(team),
        sortOrder: 100 + team,
        shot: roleLabel(role),
        attempts: 1,
        failed,
        points,
        average: points,
        goal,
        goalAverage: goal,
        pointsAdded,
      });
    };

    const addDefensiveAttempt = (row: KitchenArrivalRow, shotNumber: number) => {
      const role = roleFromShotNumber(shotNumber);
      const playerId = playerForShot(row, shotNumber);
      if (playerId == null) return;
      const team = teamForPlayer(playerId);
      const opponent = otherTeam(team);
      const opponentPoints = rowPointForTeam(row, opponent) ? 1 : 0;
      const failed = terminalShotMissed(row, shotNumber) ? 1 : 0;
      const opponentGoal =
        opponentPoints ||
        (row.servingTeam === opponent && row.servingArrivalSuccess === true)
          ? 1
          : 0;
      const pointsAdded = scoringRate - opponentPoints;
      playerRaw[playerId][role] = rawValue(playerRaw[playerId], role) + pointsAdded;
      evidence.push({
        id: `${row.rallyIndex}:${shotNumber}:${playerId}:prevented`,
        playerId,
        team,
        kind: "prevented",
        role,
        row,
        shotNumber,
        contribution: pointsAdded,
        scored: opponentPoints > 0,
        goal: opponentGoal > 0,
        failed: failed > 0,
      });
      addStat(defensive, `p${playerId}:${role}`, {
        label: playerBadge(playerId),
        sortOrder: playerSortOrder(playerId),
        shot: roleLabel(role),
        attempts: 1,
        failed,
        points: opponentPoints,
        average: opponentPoints,
        goal: opponentGoal,
        goalAverage: opponentGoal,
        pointsAdded,
      });
      addStat(defensive, `t${team}:${role}`, {
        label: teamBadge(team),
        sortOrder: 100 + team,
        shot: roleLabel(role),
        attempts: 1,
        failed,
        points: opponentPoints,
        average: opponentPoints,
        goal: opponentGoal,
        goalAverage: opponentGoal,
        pointsAdded,
      });
    };

    for (const row of serveAttemptRows) {
      for (const shotNumber of rowShotNumbers(row)) {
        if (shotNumber % 2 === 1) {
          addOffensiveAttempt(row, shotNumber);
        } else {
          addDefensiveAttempt(row, shotNumber);
        }
      }
    }

    const firstThird = new Map<string, ComboRow>();
    const thirdFifth = new Map<string, ComboRow>();
    const secondFourth = new Map<string, ComboRow>();
    const allOffensivePairs = new Map<string, ComboRow>();
    const allDefensivePairs = new Map<string, ComboRow>();

    for (const row of serveAttemptRows) {
      for (const firstShot of rowShotNumbers(row)) {
        const secondShot = firstShot + 2;
        const firstPlayer = playerForShot(row, firstShot);
        const secondPlayer = playerForShot(row, secondShot);
        if (firstPlayer == null || secondPlayer == null) continue;
        if (teamForPlayer(firstPlayer) !== teamForPlayer(secondPlayer)) continue;

        const team = teamForPlayer(firstPlayer);
        if (firstShot % 2 === 1) {
          const points = rowPointForTeam(row, team) ? 1 : 0;
          const combo = {
            key: `${firstShot}:${secondShot}:${firstPlayer}:${secondPlayer}`,
            first: (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}>
                <Box component="span" sx={{ opacity: 0.7 }}>{ordinal(firstShot)}</Box>
                {playerBadge(firstPlayer)}
              </Box>
            ),
            firstSortOrder: firstShot * 10 + playerSortOrder(firstPlayer),
            firstTeam: team,
            second: (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}>
                <Box component="span" sx={{ opacity: 0.7 }}>{ordinal(secondShot)}</Box>
                {playerBadge(secondPlayer)}
              </Box>
            ),
            secondSortOrder: secondShot * 10 + playerSortOrder(secondPlayer),
            attempts: 1,
            failed: terminalShotMissed(row, secondShot) ? 1 : 0,
            points,
            average: points,
            pointsAdded: points - scoringRate,
          };
          addCombo(allOffensivePairs, combo);
          if (firstShot === 1 && secondShot === 3) {
            addCombo(firstThird, combo);
          } else if (firstShot === 3 && secondShot === 5) {
            addCombo(thirdFifth, combo);
          }
        } else {
          const opponentPoints = rowPointForTeam(row, otherTeam(team)) ? 1 : 0;
          const combo = {
            key: `${firstShot}:${secondShot}:${firstPlayer}:${secondPlayer}`,
            first: (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}>
                <Box component="span" sx={{ opacity: 0.7 }}>{ordinal(firstShot)}</Box>
                {playerBadge(firstPlayer)}
              </Box>
            ),
            firstSortOrder: firstShot * 10 + playerSortOrder(firstPlayer),
            firstTeam: team,
            second: (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.45 }}>
                <Box component="span" sx={{ opacity: 0.7 }}>{ordinal(secondShot)}</Box>
                {playerBadge(secondPlayer)}
              </Box>
            ),
            secondSortOrder: secondShot * 10 + playerSortOrder(secondPlayer),
            attempts: 1,
            failed: terminalShotMissed(row, secondShot) ? 1 : 0,
            points: opponentPoints,
            average: opponentPoints,
            pointsAdded: scoringRate - opponentPoints,
          };
          addCombo(allDefensivePairs, combo);
          if (firstShot === 2 && secondShot === 4) {
            addCombo(secondFourth, combo);
          }
        }
      }
    }

    const kitchen = new Map<string, KitchenRow>();
    for (const row of rows) {
      if (row.servingArrivalSuccess !== true) continue;
      if (row.servingTeam !== 0 && row.servingTeam !== 1) continue;
      const team = row.servingTeam;
      const winner = pointTeam(row) ?? row.winnerTeam;
      const wins = winner === team ? 1 : 0;
      addKitchen(kitchen, {
        key: `t${team}`,
        label: teamBadge(team),
        attempts: 1,
        wins,
        average: wins,
        pointsAdded: wins - 0.5,
      });
    }

    const summary = PLAYER_IDS.map((playerId) => {
      const raw = playerRaw[playerId];
      const offense = rawAverageForRoles(raw, isCreatedRole);
      const defense = rawAverageForRoles(raw, isPreventedRole);
      return {
        playerId,
        sortOrder: playerSortOrder(playerId),
        total: offense + defense,
        raw,
        offense,
        defense,
      };
    });

    return {
      scoringRate,
      goalRate,
      teamPoints,
      finalScore: finalScore ?? [teamPoints[0], teamPoints[1]],
      offensive: Array.from(offensive.values()),
      defensive: Array.from(defensive.values()),
      firstThird: Array.from(firstThird.values()),
      thirdFifth: Array.from(thirdFifth.values()),
      secondFourth: Array.from(secondFourth.values()),
      allOffensivePairs: Array.from(allOffensivePairs.values()),
      allDefensivePairs: Array.from(allDefensivePairs.values()),
      kitchen: Array.from(kitchen.values()),
      summary,
      evidence,
      pointRows,
      serveAttemptRows,
    };
  }, [finalScore, playerBadge, playerSortOrder, rows, teamBadge, teamForPlayer]);

  const entryShotNumbers = React.useMemo(() => {
    const maxShot = Math.max(5, ...rows.flatMap(rowShotNumbers), 0);
    return Array.from({ length: maxShot }, (_, index) => index + 1);
  }, [rows]);

  const entryRows = React.useMemo(
    () =>
      rows.map((row) => ({
        key: `${row.rallyIndex}:${row.pointKey ?? row.startMs}`,
        row,
        shots: Object.fromEntries(
          entryShotNumbers.map((shotNumber) => [
            shotNumber,
            playerForShot(row, shotNumber),
          ]),
        ) as Record<number, PlayerId | null>,
        missedShotNumber: terminalMissedShotNumber(row),
      })),
    [entryShotNumbers, rows],
  );

  const [shotPreview, setShotPreview] =
    React.useState<PointsAddedShotPreview | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const handleOpenShotPreview = React.useCallback(
    (payload: OpenShotPreviewPayload) => {
      const nonce = Date.now();
      setShotPreview({
        row: payload.row,
        shotNumber: payload.shotNumber,
        title: payload.title,
        context: payload.context,
        timeSec: payload.timeSec,
        loop: payload.loop ? { ...payload.loop, nonce } : null,
        nonce,
      });
    },
    [],
  );

  const exportRootRef = React.useRef<HTMLDivElement | null>(null);
  const handleExportHtml = React.useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || exporting) return;

    const title =
      variant === "text" ? "Points Added Text Report" : "Points Added Report";
    const baseFilename = safeFilenamePart(packageId);
    const noVideoHtmlFilename = `${baseFilename}_points_added_report.html`;
    const htmlFilename = `${baseFilename}_points_added_report_with_video.html`;
    const exportEvidence = buildPointsAddedExportEvidence(model, playerText);
    const videoUrls = Array.from(
      new Set(
        exportEvidence
          .map((item) => item.videoUrl)
          .filter((url): url is string => Boolean(url)),
      ),
    );

    setExporting(true);
    try {
      const generatedAt = new Date().toISOString();
      const noVideoDataJson = jsonForInlineScript({
        version: 1,
        generatedAt,
        videos: {},
        errors: {},
        videoOmitted: true,
      });
      const inlineStyles = collectInlineStylesForExport();
      const exportedRootHtml = sanitizeHtmlForExport(root.outerHTML);

      const makeHtml = (
        inlineVideoDataJson: string,
        videoMode: "embedded" | "none",
      ) => {
        const rootHtml = exportedRootHtml;
        const reportEvidence =
          videoMode === "embedded"
            ? exportEvidence
            : exportEvidence.map((item) => ({ ...item, videoUrl: null }));
        const reportData = {
          version: 1,
          packageId: packageId ?? null,
          title,
          generatedAt,
          videoMode,
          evidence: reportEvidence,
        };
        const modeNote =
          videoMode === "none"
            ? '<div class="pa-export-mode-note"><b>No-video export.</b> Evidence tables and Watch buttons are preserved, but clip payloads were omitted so this file stays small and avoids local-file video loading problems. Open the matching <code>_with_video.html</code> file for playable clips.</div>'
            : '<div class="pa-export-mode-note"><b>Full embedded-video export.</b> This file includes base64 clip payloads and may trigger browser <code>file://</code> security warnings or slow initial loading. The plain <code>_points_added_report.html</code> export is the safer review copy.</div>';

        return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  ${inlineStyles}
  <style>
    body {
      margin: 0;
      padding: 24px;
      background: ${variant === "text" ? "#090909" : "#f7f7f0"};
      color: ${variant === "text" ? "rgba(255,255,255,0.92)" : "#111"};
      font-family: Arial, Helvetica, sans-serif;
    }
    .points-added-export-shell {
      max-width: 1100px;
      margin: 0 auto;
    }
    .pa-export-mode-note {
      max-width: 1100px;
      margin: 0 auto 12px;
      padding: 10px 12px;
      border: 1px solid rgba(0,0,0,0.24);
      border-radius: 4px;
      background: #fff7bf;
      color: #111;
      font-size: 13px;
      line-height: 1.35;
    }
    .points-added-entry-scroll {
      max-height: min(72vh, 620px) !important;
      overflow: auto !important;
    }
    .points-added-entry-scroll thead th {
      position: sticky !important;
      top: 0 !important;
      z-index: 5 !important;
      background: #dcf4d3 !important;
      box-shadow: 0 1px 0 rgba(0,0,0,0.18);
    }
    .pa-export-img-placeholder {
      display: inline-block;
      width: 28px;
      height: 28px;
      border: 1px solid rgba(0,0,0,0.35);
      border-radius: 6px;
      background: linear-gradient(135deg, #e4f0dd, #9fb2a1);
      vertical-align: middle;
    }
    .pa-export-popup {
      position: fixed;
      z-index: 2147483000;
      background: #101010;
      color: #fff;
      border: 1px solid rgba(255,255,255,0.28);
      border-radius: 6px;
      box-shadow: 0 18px 70px rgba(0,0,0,0.55);
      overflow: hidden;
    }
    .pa-export-popup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
      background: #1e1e1e;
      border-bottom: 1px solid rgba(255,255,255,0.16);
      cursor: move;
      user-select: none;
    }
    .pa-export-popup-title {
      font-size: 16px;
      font-weight: 800;
      line-height: 1.2;
    }
    .pa-export-popup-subtitle {
      margin-top: 3px;
      font-size: 12px;
      color: rgba(255,255,255,0.72);
    }
    .pa-export-popup-close {
      flex: 0 0 auto;
      width: 28px;
      height: 28px;
      border: 1px solid rgba(255,255,255,0.24);
      border-radius: 4px;
      background: rgba(255,255,255,0.08);
      color: #fff;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
    }
    .pa-export-popup-body {
      padding: 12px;
      max-height: calc(100vh - 160px);
      overflow: auto;
    }
    .pa-export-evidence-table {
      width: 100%;
      border-collapse: collapse;
      background: #dcf4d3;
      color: #111;
      font-size: 12px;
    }
    .pa-export-evidence-table th,
    .pa-export-evidence-table td {
      padding: 7px 8px;
      border-bottom: 1px solid rgba(0,0,0,0.12);
      text-align: left;
      vertical-align: middle;
    }
    .pa-export-evidence-table th {
      font-weight: 800;
      background: rgba(255,255,255,0.28);
      position: sticky;
      top: 0;
    }
    .pa-export-evidence-table tr.pa-good td {
      background: #f7f7cf;
    }
    .pa-export-evidence-table tr.pa-bad td {
      background: #fce4d6;
    }
    .pa-export-philosophy {
      margin: 0 0 10px;
      padding: 8px 10px;
      border: 1px solid rgba(0,0,0,0.16);
      background: rgba(255,255,255,0.52);
      color: #111;
      font-size: 12px;
      line-height: 1.35;
    }
    .pa-export-philosophy summary,
    .pa-export-math summary {
      cursor: pointer;
      font-weight: 900;
    }
    .pa-export-philosophy p {
      margin: 7px 0 0;
    }
    .pa-export-math {
      margin-top: 4px;
      color: #222;
      font-size: 12px;
      line-height: 1.3;
    }
    .pa-export-math div {
      margin-top: 3px;
    }
    .pa-export-watch,
    .pa-export-video-actions button {
      min-height: 26px;
      border: 1px solid rgba(0,0,0,0.32);
      border-radius: 4px;
      background: rgba(255,255,255,0.65);
      color: #111;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
    }
    .pa-export-video {
      display: block;
      width: 100%;
      max-height: min(70vh, 620px);
      background: #000;
    }
    .pa-export-video-actions {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 8px;
      color: rgba(255,255,255,0.8);
      font-size: 12px;
    }
    .pa-export-video-missing {
      padding: 14px;
      color: #fff;
      background: rgba(255,255,255,0.08);
      border: 1px solid rgba(255,255,255,0.16);
      line-height: 1.45;
    }
    @media print {
      body { padding: 0; background: #fff; }
      .points-added-export-shell { max-width: none; }
    }
  </style>
</head>
<body>
  ${modeNote}
  <div class="points-added-export-shell">${rootHtml}</div>
  <script>window.POINTS_ADDED_REPORT_DATA = ${jsonForInlineScript(reportData)};</script>
  <script type="application/json" id="points-added-video-data-json">${inlineVideoDataJson}</script>
  <script>${pointsAddedExportRuntimeScript()}</script>
</body>
</html>`;
      };

      const noVideoHtml = makeHtml(noVideoDataJson, "none");

      downloadTextFile(
        noVideoHtmlFilename,
        noVideoHtml,
        "text/html;charset=utf-8",
      );

      const shouldExportVideo =
        videoUrls.length > 0 &&
        window.confirm(
          "Also export the embedded-video version? It can be very large and may take a while to generate.",
        );
      if (!shouldExportVideo) return;

      const videoData = await fetchVideoDataUrls(videoUrls);
      const videoDataPayload = {
        version: 1,
        generatedAt,
        videos: videoData.videos,
        errors: videoData.errors,
      };
      const videoDataJson = jsonForInlineScript(videoDataPayload);
      const html = makeHtml(videoDataJson, "embedded");
      window.setTimeout(() => {
        downloadTextFile(htmlFilename, html, "text/html;charset=utf-8");
      }, 150);
    } finally {
      setExporting(false);
    }
  }, [exporting, model, packageId, playerText, variant]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperComponent={PointsAddedDraggablePaper}
      PaperProps={{
        sx: {
          width: "min(1480px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 32px)",
          bgcolor: variant === "text" ? "#090909" : "#f7f7f0",
          color: variant === "text" ? "rgba(255,255,255,0.92)" : "#111",
          border:
            variant === "text"
              ? "1px solid rgba(255,255,255,0.18)"
              : "1px solid rgba(0,0,0,0.18)",
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        id={POINTS_ADDED_DIALOG_TITLE_ID}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.25,
          cursor: "move",
          borderBottom:
            variant === "text"
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.14)",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
            {variant === "text" ? "Points Added Text Report" : "Points Added Report"}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.66 }}>
            PBV rally-value {variant === "text" ? "writeup" : "tables"} from rally data
            {packageId ? ` · ${packageId}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleExportHtml}
            disabled={exporting}
            sx={{
              minHeight: 28,
              borderColor:
                variant === "text"
                  ? "rgba(255,255,255,0.34)"
                  : "rgba(0,0,0,0.32)",
              color: variant === "text" ? "white" : "#111",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            {exporting ? "Exporting..." : "Export HTML"}
          </Button>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: variant === "text" ? "white" : "#111" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        <Box
          ref={exportRootRef}
          sx={{
            display: "grid",
            gap: 1.25,
            maxWidth: variant === "text" ? "none" : 1020,
            mx: variant === "text" ? 0 : "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              color: "rgba(255,255,255,0.72)",
              fontSize: 12,
            }}
          >
            <Metric label="Rallies" value={String(rows.length)} />
            <Metric label="Serve attempts" value={String(model.serveAttemptRows.length)} />
            <Metric label="Scored points" value={String(model.pointRows.length)} />
            <Metric label="Observed scoring average" value={pct(model.scoringRate)} />
            <Metric label="Observed kitchen/point goal" value={pct(model.goalRate)} />
          </Box>

          {variant === "text" ? (
              <TextReport
                model={model}
                rows={rows}
                playerText={playerText}
                teamText={displayTeamText}
              />
          ) : (
            <>
              <ReportIntro packageId={packageId} />
              <ReportHeader
                model={model}
                playerSortOrder={playerSortOrder}
                playerBadge={playerBadge}
                teamBadge={teamBadge}
              />
              <NutshellSummary
                model={model}
                playerBadge={playerBadge}
                playerText={playerText}
                teamBadge={teamBadge}
                teamForPlayer={teamForPlayer}
                onOpenRallyShot={handleOpenShotPreview}
              />
              <Section title="Analysis Framework">
                <TextBlock tone="report">
                  PBV data supplies the same core worksheet fields as the manual
                  chart, then extends them across every tagged shot in the rally:
                  shot hitter, missed/faulted shots, whether the serving team
                  reached the kitchen, and whether the serving team scored.
                </TextBlock>
              </Section>
              <Section title="Data Entry Worksheet">
                <EntryTable
                  rows={entryRows}
                  shotNumbers={entryShotNumbers}
                  playerBadge={playerBadge}
                  teamBadge={teamBadge}
                  teamForPlayer={teamForPlayer}
                  onOpenRallyShot={handleOpenShotPreview}
                />
              </Section>

              <Section title="The Worksheet - Offense">
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.25,
                  }}
                >
                  <StatsTable
                    title="Offensive Statistics"
                    rows={model.offensive.filter((row) => row.key.startsWith("p"))}
                    pointHeader="Points"
                    averageHeader="Scoring Avg"
                    subjectHeader="Player"
                    note="Goal = serving team scored the point or reached the kitchen. Goal Avg = Goal divided by Attempts."
                  />
                  <StatsTable
                    title="Offensive Statistics - Team"
                    rows={model.offensive.filter((row) => row.key.startsWith("t"))}
                    pointHeader="Points"
                    averageHeader="Scoring Avg"
                    subjectHeader="Team"
                    note="Goal = serving team scored the point or reached the kitchen. Goal Avg = Goal divided by Attempts."
                  />
                  <OffensiveDiscussion
                    model={model}
                    playerText={playerText}
                    teamText={displayTeamText}
                  />
                </Box>
              </Section>

              <Section title="The Worksheet - Offensive Combinations">
                <Box sx={{ display: "grid", gap: 1.25 }}>
                  <ComboTable
                    title="Offensive Combinations: Same-Side Shot Pairs"
                    firstHeader="First shot"
                    secondHeader="Next same-side shot"
                    rows={model.allOffensivePairs}
                    pointHeader="Points"
                    averageHeader="Scoring Avg"
                  />
                  <TextBlock>
                    This extends the PDF&apos;s “First vs Third” and “Third vs
                    Fifth” idea to every same-side serving-team pair: serve to
                    third, third to fifth, fifth to seventh, and so on. Each row
                    still asks whether that serving team eventually scored.
                  </TextBlock>
                </Box>
              </Section>

              <Section title="The Worksheet - Defense">
                <Box sx={{ display: "grid", gap: 1.25 }}>
                  <StatsTable
                    title="Defensive Statistics"
                    rows={model.defensive.filter((row) => row.key.startsWith("p"))}
                    pointHeader="Opp Points"
                    averageHeader="Opp Scoring Avg"
                    subjectHeader="Player"
                    metricHeader="Points Prevented"
                    metricHelp={POINTS_PREVENTED_HELP}
                  />
                  <StatsTable
                    title="Defensive Statistics - Team"
                    rows={model.defensive.filter((row) => row.key.startsWith("t"))}
                    pointHeader="Opp Points"
                    averageHeader="Opp Scoring Avg"
                    subjectHeader="Team"
                    metricHeader="Points Prevented"
                    metricHelp={POINTS_PREVENTED_HELP}
                  />
                </Box>
              </Section>

              <Section title="The Worksheet - Defensive Combinations">
                <ComboTable
                  title="Defensive Combinations: Same-Side Shot Pairs"
                  firstHeader="First shot"
                  secondHeader="Next same-side shot"
                  rows={model.allDefensivePairs}
                  pointHeader="Opp Points"
                  averageHeader="Opp Scoring Avg"
                  metricHeader="Points Prevented"
                  metricHelp={POINTS_PREVENTED_HELP}
                />
              </Section>

              <Section title="Kitchen Points">
                <KitchenTable rows={model.kitchen} />
                <Typography sx={{ mt: 0.75, fontSize: 12, color: "#111" }}>
                  Serves ending in kitchen rallies:{" "}
                  {pct(
                    rows.length > 0
                      ? rows.filter((row) => row.servingArrivalSuccess === true)
                          .length / rows.length
                      : null,
                  )}
                </Typography>
              </Section>

              <Section title="Points Added: Summary Table">
                <SummaryTable rows={model.summary} playerBadge={playerBadge} />
                <Typography sx={{ mt: 0.75, fontSize: 12, color: "#111" }}>
                  {pct(model.scoringRate)} &lt;----- Probability of Scoring a Point
                </Typography>
              </Section>

              <Section title="Points Added: Notes">
                <TextReport
                  model={model}
                  rows={rows}
                  playerText={playerText}
                  teamText={displayTeamText}
                  reportTone
                />
              </Section>
            </>
          )}
        </Box>
      </DialogContent>
      {shotPreview ? (
        <PointsAddedShotPreviewPopup
          preview={shotPreview}
          packageId={packageId ?? ""}
          rows={rows}
          cvJson={cvJson ?? null}
          renderPlayerIdentity={renderPlayerIdentity}
          insightsToCvPlayerIndex={insightsToCvPlayerIndex}
          firefightsClips={firefightsClips}
          firefightRallySet={firefightRallySet}
          onOpenShotTargetLab={
            onOpenShotTargetLab
              ? () => {
                  onOpenShotTargetLab(shotPreview.row, shotPreview.shotNumber);
                  setShotPreview(null);
                }
              : undefined
          }
          onClose={() => setShotPreview(null)}
        />
      ) : null}
    </Dialog>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.65,
        border: "1px solid rgba(0,0,0,0.12)",
        borderRadius: 0.5,
        bgcolor: "#dcf4d3",
      }}
    >
      <Box component="span" sx={{ opacity: 0.72, mr: 0.75 }}>
        {props.label}
      </Box>
      <Box component="span" sx={{ color: "#111", fontWeight: 800 }}>
        {props.value}
      </Box>
    </Box>
  );
}

function PointsAddedShotPreviewPopup(props: {
  preview: PointsAddedShotPreview;
  packageId: string;
  rows: KitchenArrivalRow[];
  cvJson: CvJson | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  onOpenShotTargetLab?: () => void;
  onClose: () => void;
}) {
  const [pos, setPos] = React.useState(() => ({
    x: Math.max(24, Math.min(window.innerWidth - 780, 96)),
    y: Math.max(24, Math.min(window.innerHeight - 520, 84)),
  }));

  const handleDragStart = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const start = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        x: pos.x,
        y: pos.y,
      };

      const onMove = (moveEvent: MouseEvent) => {
        const nextX = start.x + moveEvent.clientX - start.mouseX;
        const nextY = start.y + moveEvent.clientY - start.mouseY;
        setPos({
          x: Math.max(8, Math.min(window.innerWidth - 360, nextX)),
          y: Math.max(8, Math.min(window.innerHeight - 220, nextY)),
        });
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos.x, pos.y],
  );

  const playbackSeed = React.useMemo(
    () => ({
      timeSec: Math.max(0, props.preview.timeSec ?? 0),
      paused: true,
      nonce: props.preview.nonce,
    }),
    [props.preview.nonce, props.preview.timeSec],
  );
  const previewRow = React.useMemo(
    () => rowWithPreviewShotTiming(props.preview.row),
    [props.preview.row, props.preview.nonce],
  );

  return (
    <Box
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        zIndex: 15000,
        width: "min(780px, calc(100vw - 32px))",
        bgcolor: "#0a0a0a",
        color: "white",
        border: "1px solid rgba(255,255,255,0.28)",
        boxShadow: "0 18px 70px rgba(0,0,0,0.65)",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        onMouseDown={handleDragStart}
        sx={{
          px: 1,
          py: 0.75,
          cursor: "move",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "rgba(255,255,255,0.08)",
          borderBottom: "1px solid rgba(255,255,255,0.16)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.2 }}>
            {props.preview.title}
          </Typography>
          <Typography
            sx={{
              mt: 0.2,
              fontSize: 12,
              color: "rgba(255,255,255,0.72)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {props.preview.context}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={props.onClose}
          sx={{ color: "white" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 1 }}>
        <ActiveRallyVideo
          renderPlayerIdentity={props.renderPlayerIdentity}
          aiContextLabel="Points Added shot preview"
          insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
          focusedFilter={null}
          cvJson={props.cvJson}
          controlsPlacement="left"
          packageId={props.packageId}
          row={previewRow}
          rows={props.rows}
          showAi={false}
          showCondensedTimeline={false}
          showShotSpaceButton={Boolean(props.onOpenShotTargetLab)}
          onOpenShotSpace={props.onOpenShotTargetLab}
          showShotBookmarksButton={false}
          fontColor="white"
          activeShotNumber={props.preview.shotNumber}
          seedPlayback={playbackSeed}
          seedLoopRegion={props.preview.loop ?? undefined}
          beginningScore={previewRow.scoreBefore ?? null}
          endingScore={previewRow.scoreAfter ?? null}
          firefightsClips={props.firefightsClips}
          firefightRallySet={props.firefightRallySet}
          showRateControls={true}
          showUltraSlowRate={true}
          showLoopScrubber={true}
          defaultLoopUiOpen={true}
          disablePopout={true}
          showPopoutButton={false}
          autoPlayOnRallyChange={false}
          seekToActiveShotOnChange={false}
          forceMuted={true}
          pauseOtherVideosOnMount={false}
        />
      </Box>
    </Box>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 0,
        overflow: "hidden",
        bgcolor: "#dcf4d3",
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.75,
          bgcolor: "#dcf4d3",
          color: "#111",
          fontSize: 13,
          fontWeight: 900,
          letterSpacing: "0.02em",
        }}
      >
        {props.title}
      </Box>
      <Box sx={{ p: 1 }}>{props.children}</Box>
    </Box>
  );
}

function tableSx(firstColumnWidth: number | string = 150) {
  return {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    "& th": {
      color: "#111",
      fontSize: 11,
      fontWeight: 800,
      textAlign: "right",
      borderBottom: "1px solid rgba(0,0,0,0.16)",
      px: 0.6,
      py: 0.5,
      whiteSpace: "nowrap",
    },
    "& td": {
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      color: "#111",
      fontSize: 12,
      px: 0.6,
      py: 0.45,
      textAlign: "right",
      verticalAlign: "middle",
    },
    "& th:first-of-type, & td:first-of-type": {
      textAlign: "left",
      width: firstColumnWidth,
      whiteSpace: "nowrap",
    },
  };
}

function paSx(value: number) {
  if (value > 0.05) return { color: "#111", fontWeight: 900 };
  if (value < -0.05) return { color: "#f00", fontWeight: 900 };
  return { color: "#333", fontWeight: 800 };
}

function contributionSx(value: number) {
  if (value > 0.005) return { color: "#111", fontWeight: 900 };
  if (value < -0.005) return { color: "#f00", fontWeight: 900 };
  return { color: "#333", fontWeight: 800 };
}

function rowToneSx(value: number): any {
  if (value > 0.05) return { "& td": { bgcolor: "#f7f7cf" } };
  if (value < -0.05) return { "& td": { bgcolor: "#fce4d6" } };
  return {};
}

function PointsAddedCell(props: { value: number }) {
  return (
    <Box
      sx={{
        ...contributionSx(props.value),
        bgcolor: props.value < -0.005 ? "#fce4d6" : "#fff2cc",
        px: 0.35,
        minWidth: 36,
        display: "inline-block",
      }}
    >
      {props.value < -0.005
        ? `(${fmtContributionNumber(Math.abs(props.value))})`
        : fmtContributionNumber(props.value)}
    </Box>
  );
}

function fmtEquationNumber(value: number): string {
  if (!Number.isFinite(value)) return "-";
  if (Math.abs(value) < 0.005) return "0.00";
  return value.toFixed(2);
}

function fmtEquationSigned(value: number): string {
  if (!Number.isFinite(value)) return "-";
  if (value > 0.005) return `+${fmtEquationNumber(value)}`;
  if (value < -0.005) return `-${fmtEquationNumber(Math.abs(value))}`;
  return "0.00";
}

function EquationTerm(props: { label: React.ReactNode; title: string }) {
  return (
    <Tooltip
      title={props.title}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            fontSize: 13,
            lineHeight: 1.35,
            maxWidth: 360,
          },
        },
      }}
    >
      <Box
        component="abbr"
        title={props.title}
        sx={{
          cursor: "help",
          color: "#111",
          fontWeight: 900,
          textDecoration: "underline dotted",
          textUnderlineOffset: "2px",
        }}
      >
        {props.label}
      </Box>
    </Tooltip>
  );
}

function MatchExpectationPhilosophy(props: {
  baseline: number | null;
  servingPoints?: number | null;
  serveAttempts?: number | null;
}) {
  const baselineText =
    props.baseline == null ? "the observed serving-team scoring rate" : pct(props.baseline);
  const countText =
    props.servingPoints != null && props.serveAttempts != null && props.serveAttempts > 0
      ? `${props.servingPoints} scoring service rallies divided by ${props.serveAttempts} total service rallies`
      : null;
  return (
    <Box
      component="details"
      sx={{
        bgcolor: "rgba(255,255,255,0.38)",
        border: "1px solid rgba(0,0,0,0.12)",
        color: "#111",
        "& summary": {
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 900,
          px: 0.75,
          py: 0.45,
          listStylePosition: "inside",
        },
      }}
    >
      <summary>What is expected chance of winning rally?</summary>
      <Box sx={{ px: 0.9, pb: 0.8, display: "grid", gap: 0.45 }}>
        <Typography sx={{ color: "#222", fontSize: 11.7, lineHeight: 1.38 }}>
          <b>Expected chance of winning rally</b> is this game&apos;s combined
          serving-team scoring rate across both teams. In other words, it pools
          Team 1&apos;s service rallies and Team 2&apos;s service rallies, then asks:
          how often did the serving side score in this particular game? It is
          the report&apos;s in-game comparison point, not a universal pickleball
          average. For this report view, that comparison point is {baselineText}
          {countText ? `: ${countText}.` : "."}
        </Typography>
        <Typography sx={{ color: "#222", fontSize: 11.7, lineHeight: 1.38 }}>
          The calculation uses that value as a neutral in-game bar. When the serving
          team scores, the serving-side touches get credit for beating the bar. When
          the serving team does not score, the receiving-side touches get credit for
          holding the server below the bar. That rally contribution is then split
          across the tagged touches for that side.
        </Typography>
        <Typography sx={{ color: "#222", fontSize: 11.7, lineHeight: 1.38 }}>
          The original Mathlete-style examples often compare rows to a broader
          expected scoring number, such as a historical pickleball baseline. That
          answers, "was this above a general expectation?" This top nutshell instead
          answers, "inside this game, which players and touches tilted rallies away
          from this game&apos;s match pattern?"
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 11.4, lineHeight: 1.35 }}>
          Tradeoff: this expected-chance read puts both teams on the same
          in-game scale and keeps the summary tied to the scoreboard, but it is not an
          absolute skill rating. If both teams played unusually well or unusually
          poorly, this in-game bar moves with them. A historical benchmark can still
          be a useful second lens later.
        </Typography>
      </Box>
    </Box>
  );
}

function ShareMathDetails(props: { item: PointsAddedEvidenceItem }) {
  const formula = props.item.formula;
  if (!formula) return null;
  const isCreated = formula.side === "created";
  const firstValue = isCreated ? formula.servingPoint : formula.baseline;
  const secondValue = isCreated ? formula.baseline : formula.servingPoint;
  const firstLabel = isCreated ? "server result" : "expected chance of winning rally";
  const secondLabel = isCreated ? "expected chance of winning rally" : "server result";
  const sideLabel = isCreated ? "serving side" : "receiving side";
  const matchExpectationHelp =
    "Expected chance of winning rally: this game's serving-team scoring rate across both teams combined. It pools all service rallies by Team 1 and Team 2, so both sides are compared to the same in-game bar.";
  const firstHelp = isCreated
    ? "This rally: the serving team scored, so the rally result is 1. If the serving team had failed to score, this would be 0."
    : matchExpectationHelp;
  const secondHelp = isCreated
    ? matchExpectationHelp
    : "This rally: the serving team scored, so the rally result is 1. If the serving team had failed to score, this would be 0.";
  const divisorHelp = isCreated
    ? "Touches sharing credit: all tagged serving-team shots in this rally. The rally credit is split across those touches so the rally is counted once."
    : "Touches sharing credit: all tagged receiving-team shots in this rally. The rally prevention credit is split across those touches so the rally is counted once.";
  const rallyResultText =
    formula.servingPoint > 0.5
      ? "server scored"
      : "server did not score";
  const baselineText = pct(formula.baseline);
  const baselineCountText =
    formula.baselineServeAttempts > 0
      ? `${formula.baselineServingPoints} of ${formula.baselineServeAttempts} service rallies`
      : "";
  const touchText = isCreated
    ? `${formula.divisor} serving-team tagged ${formula.divisor === 1 ? "touch" : "touches"}`
    : `${formula.divisor} receiving-team tagged ${formula.divisor === 1 ? "touch" : "touches"}`;
  const resultValueText = fmtEquationNumber(formula.servingPoint);
  const deltaText = fmtEquationSigned(formula.delta);
  const contributionText = fmtEquationSigned(formula.share);

  return (
    <Box
      component="details"
      sx={{
        mt: 0.25,
        color: "#111",
        "& summary": {
          cursor: "pointer",
          fontSize: 11.5,
          fontWeight: 900,
          lineHeight: 1.2,
          listStylePosition: "inside",
        },
      }}
    >
      <summary>calculation</summary>
      <Box
        sx={{
          mt: 0.35,
          p: 0.55,
          bgcolor: "rgba(255,255,255,0.42)",
          border: "1px solid rgba(0,0,0,0.10)",
          display: "grid",
          gap: 0.35,
          textAlign: "left",
          minWidth: 260,
        }}
      >
        <Typography sx={{ color: "#333", fontSize: 12, lineHeight: 1.35 }}>
          This row asks how much this shot contributed for the {sideLabel}, then
          splits that contribution across the tagged touches on that side.
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 12, lineHeight: 1.35 }}>
          <b>Server result:</b> {rallyResultText} ({resultValueText}).
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 12, lineHeight: 1.35 }}>
          <b>Expected chance of winning rally:</b> {baselineText}
          {baselineCountText
            ? ` because the serving side scored on ${baselineCountText} across both teams.`
            : "."}
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 12, lineHeight: 1.35 }}>
          <b>Rally value for the {sideLabel}:</b>{" "}
          {fmtEquationNumber(firstValue)} - {fmtEquationNumber(secondValue)} ={" "}
          {deltaText}.
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 12, lineHeight: 1.35 }}>
          This contribution is split across {touchText}, so this row gets{" "}
          <Box component="span" sx={contributionSx(formula.share)}>
            {contributionText}
          </Box>
          .
        </Typography>
        <Typography sx={{ color: "#111", fontSize: 12.2, lineHeight: 1.45 }}>
          <EquationTerm
            label="contribution to total"
            title="The value added to this evidence row and to the expanded total above."
          />{" "}
          = ( <EquationTerm label={firstLabel} title={firstHelp} /> -{" "}
          <EquationTerm label={secondLabel} title={secondHelp} /> ) /{" "}
          <EquationTerm label="touches" title={divisorHelp} />
        </Typography>
        <Typography sx={{ color: "#111", fontSize: 12.2, lineHeight: 1.45 }}>
          = ( {fmtEquationNumber(firstValue)} - {fmtEquationNumber(secondValue)} ) /{" "}
          {formula.divisor} = {fmtEquationSigned(formula.delta)} / {formula.divisor} ={" "}
          <Box component="span" sx={contributionSx(formula.share)}>
            {fmtEquationSigned(formula.share)}
          </Box>
        </Typography>
        <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.3 }}>
          Table rounds this contribution to {fmtContributionSigned(formula.share)}.
        </Typography>
      </Box>
    </Box>
  );
}

function roleRankFromKey(key: string): number {
  const role = key.split(":").slice(1).join(":") as Role | undefined;
  return role ? roleRank(role) : 9;
}

function orderedStatRows(rows: StatRow[]): StatRow[] {
  return [...rows].sort((a, b) => {
    const aTeam = a.key.startsWith("t") ? 1 : 0;
    const bTeam = b.key.startsWith("t") ? 1 : 0;
    if (aTeam !== bTeam) return aTeam - bTeam;
    const aRole = roleRankFromKey(a.key);
    const bRole = roleRankFromKey(b.key);
    if (aRole !== bRole) return aRole - bRole;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return 0;
  });
}

function orderedComboRows(rows: ComboRow[]): ComboRow[] {
  return [...rows].sort((a, b) => {
    if (a.firstSortOrder !== b.firstSortOrder) {
      return a.firstSortOrder - b.firstSortOrder;
    }
    if (a.secondSortOrder !== b.secondSortOrder) {
      return a.secondSortOrder - b.secondSortOrder;
    }
    return a.key.localeCompare(b.key);
  });
}

function TableTitle(props: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 900, mb: 0.6, color: "#111" }}>
      {props.children}
    </Typography>
  );
}

const POINTS_ADDED_HELP =
  "Raw Points Added = attempts times the gap between this row's scoring rate and the overall scoring rate for the charted game. Positive means this row beat expectation; negative means it trailed expectation. Rows can double-count a rally across multiple serving-team shots.";
const POINTS_PREVENTED_HELP =
  "Points Prevented = attempts times the gap between the overall scoring rate for the charted game and the opponent's scoring rate for this row. Positive means this row suppressed opponent scoring; negative means opponents scored more than expected.";

function PointsAddedHeader(props: { label?: string; help?: string }) {
  return (
    <Tooltip title={props.help ?? POINTS_ADDED_HELP} arrow placement="top">
      <Box
        component="span"
        sx={{
          cursor: "help",
          textDecoration: "underline dotted",
          textUnderlineOffset: "2px",
        }}
      >
        {props.label ?? "Pts Added"}
      </Box>
    </Tooltip>
  );
}

function HelpHeader(props: { label: string; help: string }) {
  return (
    <Tooltip title={props.help} arrow placement="top">
      <Box
        component="span"
        sx={{
          cursor: "help",
          textDecoration: "underline dotted",
          textUnderlineOffset: "2px",
        }}
      >
        {props.label}
      </Box>
    </Tooltip>
  );
}

function ReportIntro(props: { packageId?: string | null }) {
  return (
    <Box
      sx={{
        bgcolor: "#f7f7f0",
        border: "1px solid rgba(0,0,0,0.12)",
        p: 2,
      }}
    >
      <Typography sx={{ color: "#008000", fontWeight: 900, fontSize: 18 }}>
        Points Added Match Analysis
      </Typography>
      <Typography sx={{ mt: 0.4, color: "#111", fontSize: 13 }}>
        PBV-assisted Points Added report{props.packageId ? ` · ${props.packageId}` : ""}
      </Typography>
      <Typography sx={{ mt: 1.25, color: "#111", fontSize: 13, lineHeight: 1.45 }}>
        This expands from the Pickleball Mathlete-style worksheet/report structure:
        chart the opening shots, extend the same idea across every tagged shot,
        derive offensive and defensive scoring rates, evaluate kitchen-rally
        outcomes, and summarize raw plus averaged Points Added by player.
      </Typography>
    </Box>
  );
}

function ReportHeader(props: {
  model: PointsAddedModel;
  playerSortOrder: (playerId: PlayerId) => number;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  teamBadge: (team: Team01) => React.ReactNode;
}) {
  const players = [...PLAYER_IDS].sort(
    (a, b) => props.playerSortOrder(a) - props.playerSortOrder(b),
  );
  return (
    <Section title="Match Statistics">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          alignItems: "start",
          mb: 1.5,
        }}
      >
        <Box component="table" sx={{ ...tableSx(), tableLayout: "auto" }}>
          <thead>
            <tr>
              <th>Players</th>
            </tr>
          </thead>
          <tbody>
            {players.map((playerId) => (
              <tr key={playerId}>
                <td>{props.playerBadge(playerId, 38)}</td>
              </tr>
            ))}
          </tbody>
        </Box>

        <Box component="table" sx={{ ...tableSx(), tableLayout: "auto" }}>
          <thead>
            <tr>
              <th>Team</th>
              <th>Total Points</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{props.teamBadge(0)}</td>
              <td>{props.model.finalScore[0]}</td>
            </tr>
            <tr>
              <td>{props.teamBadge(1)}</td>
              <td>{props.model.finalScore[1]}</td>
            </tr>
          </tbody>
        </Box>
      </Box>
    </Section>
  );
}

function strongestRow(rows: StatRow[]): StatRow | null {
  return [...rows].sort((a, b) => b.pointsAdded - a.pointsAdded)[0] ?? null;
}

function weakestRow(rows: StatRow[]): StatRow | null {
  return [...rows].sort((a, b) => a.pointsAdded - b.pointsAdded)[0] ?? null;
}

function describeStat(row: StatRow | null, fallback: string): string {
  if (!row) return fallback;
  const attemptsText = row.attempts === 1 ? "1 attempt" : `${row.attempts} attempts`;
  return `${row.shot}: ${fmtSigned(row.pointsAdded)} across ${attemptsText}`;
}

function bestRoleForSummary(row: SummaryRow): Role {
  return (Object.entries(row.raw) as Array<[Role, number]>).sort(
    (a, b) => b[1] - a[1],
  )[0]?.[0] ?? roleFromShotNumber(1);
}

function weakestRoleForSummary(row: SummaryRow): Role {
  return (Object.entries(row.raw) as Array<[Role, number]>).sort(
    (a, b) => a[1] - b[1],
  )[0]?.[0] ?? roleFromShotNumber(1);
}

function teamRawTotal(rows: StatRow[], team: Team01): number {
  return rows
    .filter((row) => row.key.startsWith(`t${team}:`))
    .reduce((sum, row) => sum + row.pointsAdded, 0);
}

type NutshellShareRow = {
  playerId: PlayerId;
  sortOrder: number;
  total: number;
  scoring: number;
  stopping: number;
  scoringRaw: Record<string, number>;
  stoppingRaw: Record<string, number>;
};

type NutshellTeamSnapshot = {
  team: Team01;
  score: number;
  total: number;
  scoring: number;
  stopping: number;
  scoringRaw: Record<string, number>;
  stoppingRaw: Record<string, number>;
};

type NutshellArea = {
  role: Role;
  value: number;
  kind: EvidenceKind;
};

type NutshellCalcKind = "overall" | "scoring" | "stopping";
type NutshellAudienceMode = "match" | "player";

type NutshellEvidenceOwner =
  | {
      owner: "player";
      playerId: PlayerId;
    }
  | {
      owner: "team";
      team: Team01;
    };

type NutshellEvidenceFocus = NutshellEvidenceOwner & {
  kind: EvidenceKind;
  role?: Role;
};

type EvidenceContributionDatum = {
  x: number;
  y: number;
  item: PointsAddedEvidenceItem;
};

type EvidenceRunningTotalDatum = {
  x: number;
  y: number;
  rallyContribution: number;
  runningTotal: number;
  plottedTouches: number;
  rallyShotCount: number | null;
};

function addRoleShare(raw: Record<string, number>, role: Role, value: number) {
  raw[role] = (raw[role] ?? 0) + value;
}

function roleByValue(
  raw: Record<string, number>,
  direction: "best" | "review",
): Role | null {
  const entries = Object.entries(raw) as Array<[Role, number]>;
  if (!entries.length) return null;
  return entries.sort((a, b) =>
    direction === "best" ? b[1] - a[1] : a[1] - b[1],
  )[0][0];
}

function roleValueText(raw: Record<string, number>, role: Role | null): string {
  if (!role) return "not enough tagged shots";
  return `${roleSummaryLabel(role)} ${fmtSigned(rawValue(raw, role))}`;
}

function areaByValue(
  scoringRaw: Record<string, number>,
  stoppingRaw: Record<string, number>,
  direction: "best" | "review",
): NutshellArea | null {
  const areas = [
    ...(Object.entries(scoringRaw) as Array<[Role, number]>).map(
      ([role, value]) => ({ role, value, kind: "created" as EvidenceKind }),
    ),
    ...(Object.entries(stoppingRaw) as Array<[Role, number]>).map(
      ([role, value]) => ({ role, value, kind: "prevented" as EvidenceKind }),
    ),
  ];
  if (!areas.length) return null;
  return areas.sort((a, b) =>
    direction === "best" ? b.value - a.value : a.value - b.value,
  )[0];
}

function areaText(area: NutshellArea | null): string {
  if (!area) return "not enough tagged shots";
  return `${roleSummaryLabel(area.role)} ${fmtSigned(area.value)}`;
}

function plainAreaText(area: NutshellArea | null): string {
  if (!area) return "not enough tagged shots yet";
  const verb = area.value >= 0 ? "helped on" : "hurt on";
  return `${verb} ${roleSummaryLabel(area.role)} (${fmtSigned(area.value)})`;
}

function combinedRoleShares(
  scoringRaw: Record<string, number>,
  stoppingRaw: Record<string, number>,
): Record<string, number> {
  const combined: Record<string, number> = {};
  for (const [role, value] of Object.entries(scoringRaw)) {
    addRoleShare(combined, role, value);
  }
  for (const [role, value] of Object.entries(stoppingRaw)) {
    addRoleShare(combined, role, value);
  }
  return combined;
}

function rawShareRows(raw: Record<string, number>) {
  return (Object.entries(raw) as Array<[Role, number]>)
    .filter(([, value]) => Math.abs(value) > 0.005)
    .sort((a, b) => roleRank(a[0]) - roleRank(b[0]));
}

function rawShareTotal(raw: Record<string, number>): number {
  return Object.values(raw).reduce((sum, value) => sum + value, 0);
}

function calcKindLabel(kind: NutshellCalcKind): string {
  if (kind === "scoring") return "Helped scoring";
  if (kind === "stopping") return "Stopped scoring";
  return "Overall";
}

function roleEvidenceKind(role: Role): EvidenceKind {
  return roleShotNumber(role) % 2 === 1 ? "created" : "prevented";
}

function isNutshellFocusForPlayer(
  focus: NutshellEvidenceFocus,
): focus is Extract<NutshellEvidenceFocus, { owner: "player" }> {
  return focus.owner === "player";
}

function buildNutshellShareModel(
  model: PointsAddedModel,
  playerSortOrder: (playerId: PlayerId) => number,
  teamForPlayer: (playerId: PlayerId) => Team01,
) {
  const players = new Map<PlayerId, NutshellShareRow>();
  const teams = new Map<Team01, NutshellTeamSnapshot>();
  const evidence: PointsAddedEvidenceItem[] = [];
  const baselineServeAttempts = model.serveAttemptRows.length;
  const baselineServingPoints = model.serveAttemptRows.filter(
    (row) => pointTeam(row) === row.servingTeam,
  ).length;

  for (const playerId of PLAYER_IDS) {
    players.set(playerId, {
      playerId,
      sortOrder: playerSortOrder(playerId),
      total: 0,
      scoring: 0,
      stopping: 0,
      scoringRaw: {},
      stoppingRaw: {},
    });
  }

  for (const team of [0, 1] as Team01[]) {
    teams.set(team, {
      team,
      score: model.finalScore[team],
      total: 0,
      scoring: 0,
      stopping: 0,
      scoringRaw: {},
      stoppingRaw: {},
    });
  }

  for (const row of model.serveAttemptRows) {
    if (row.servingTeam !== 0 && row.servingTeam !== 1) continue;
    const servingTeam = row.servingTeam;
    const defendingTeam = otherTeam(servingTeam);
    const servingPoint = pointTeam(row) === servingTeam ? 1 : 0;
    const scoringDelta = servingPoint - model.scoringRate;
    const stoppingDelta = -scoringDelta;
    const shotNumbers = rowShotNumbers(row);

    const scoringShots = shotNumbers
      .filter((shotNumber) => shotNumber % 2 === 1)
      .map((shotNumber) => ({
        shotNumber,
        playerId: playerForShot(row, shotNumber),
      }))
      .filter(
        (shot): shot is { shotNumber: number; playerId: PlayerId } =>
          shot.playerId != null && teamForPlayer(shot.playerId) === servingTeam,
      );
    const stoppingShots = shotNumbers
      .filter((shotNumber) => shotNumber % 2 === 0)
      .map((shotNumber) => ({
        shotNumber,
        playerId: playerForShot(row, shotNumber),
      }))
      .filter(
        (shot): shot is { shotNumber: number; playerId: PlayerId } =>
          shot.playerId != null && teamForPlayer(shot.playerId) === defendingTeam,
      );

    const scoringShare =
      scoringShots.length > 0 ? scoringDelta / scoringShots.length : 0;
    const stoppingShare =
      stoppingShots.length > 0 ? stoppingDelta / stoppingShots.length : 0;

    const scoringTeam = teams.get(servingTeam);
    if (scoringTeam) {
      scoringTeam.scoring += scoringDelta;
      scoringTeam.total += scoringDelta;
    }
    for (const shot of scoringShots) {
      const player = players.get(shot.playerId);
      const team = teams.get(servingTeam);
      const role = roleFromShotNumber(shot.shotNumber);
      evidence.push({
        id: `${row.rallyIndex}:${shot.shotNumber}:${shot.playerId}:created`,
        playerId: shot.playerId,
        team: servingTeam,
        kind: "created",
        role,
        row,
        shotNumber: shot.shotNumber,
        contribution: scoringShare,
        scored: servingPoint > 0,
        goal: servingGoal(row),
        failed: terminalShotMissed(row, shot.shotNumber),
        formula: {
          side: "created",
          servingPoint,
          baseline: model.scoringRate,
          baselineServingPoints,
          baselineServeAttempts,
          delta: scoringDelta,
          divisor: scoringShots.length,
          share: scoringShare,
        },
      });
      if (player) {
        player.scoring += scoringShare;
        player.total += scoringShare;
        addRoleShare(player.scoringRaw, role, scoringShare);
      }
      if (team) {
        addRoleShare(team.scoringRaw, role, scoringShare);
      }
    }

    const stoppingTeam = teams.get(defendingTeam);
    if (stoppingTeam) {
      stoppingTeam.stopping += stoppingDelta;
      stoppingTeam.total += stoppingDelta;
    }
    for (const shot of stoppingShots) {
      const player = players.get(shot.playerId);
      const team = teams.get(defendingTeam);
      const role = roleFromShotNumber(shot.shotNumber);
      evidence.push({
        id: `${row.rallyIndex}:${shot.shotNumber}:${shot.playerId}:prevented`,
        playerId: shot.playerId,
        team: defendingTeam,
        kind: "prevented",
        role,
        row,
        shotNumber: shot.shotNumber,
        contribution: stoppingShare,
        scored: servingPoint > 0,
        goal: servingGoal(row),
        failed: terminalShotMissed(row, shot.shotNumber),
        formula: {
          side: "prevented",
          servingPoint,
          baseline: model.scoringRate,
          baselineServingPoints,
          baselineServeAttempts,
          delta: stoppingDelta,
          divisor: stoppingShots.length,
          share: stoppingShare,
        },
      });
      if (player) {
        player.stopping += stoppingShare;
        player.total += stoppingShare;
        addRoleShare(player.stoppingRaw, role, stoppingShare);
      }
      if (team) {
        addRoleShare(team.stoppingRaw, role, stoppingShare);
      }
    }
  }

  const playerRows = Array.from(players.values()).sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const teamSnapshots = Array.from(teams.values()).sort((a, b) => a.team - b.team);
  const maxAbs = Math.max(
    1,
    ...teamSnapshots.flatMap((team) => [
      Math.abs(team.total),
      Math.abs(team.scoring),
      Math.abs(team.stopping),
    ]),
    ...playerRows.flatMap((player) => [
      Math.abs(player.total),
      Math.abs(player.scoring),
      Math.abs(player.stopping),
    ]),
  );

  return { playerRows, teamSnapshots, maxAbs, evidence };
}

function NutshellSummary(props: {
  model: PointsAddedModel;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  playerText: (playerId: PlayerId) => string;
  teamBadge: (team: Team01) => React.ReactNode;
  teamForPlayer: (playerId: PlayerId) => Team01;
  onOpenRallyShot: (payload: OpenShotPreviewPayload) => void;
}) {
  const playerSortOrder = React.useCallback(
    (playerId: PlayerId) => props.model.summary.find((row) => row.playerId === playerId)?.sortOrder ?? playerId,
    [props.model.summary],
  );
  const { playerRows, teamSnapshots, maxAbs, evidence: nutshellEvidence } = React.useMemo(
    () => buildNutshellShareModel(props.model, playerSortOrder, props.teamForPlayer),
    [playerSortOrder, props.model, props.teamForPlayer],
  );
  const totalRallies = Math.max(
    1,
    props.model.serveAttemptRows.length,
    ...props.model.serveAttemptRows.map((row) => row.rallyIndex + 1),
  );
  const scoreboardLeader =
    props.model.finalScore[0] === props.model.finalScore[1]
      ? null
      : props.model.finalScore[0] > props.model.finalScore[1]
        ? 0
        : 1;
  const edgeLeader =
    Math.abs(teamSnapshots[0].total - teamSnapshots[1].total) < 0.05
      ? null
      : teamSnapshots[0].total > teamSnapshots[1].total
        ? 0
        : 1;
  const bestPlayer =
    [...playerRows].sort((a, b) => b.total - a.total)[0] ?? null;
  const reviewPlayer =
    [...playerRows].sort((a, b) => a.total - b.total)[0] ?? null;
  const [audienceMode, setAudienceMode] =
    React.useState<NutshellAudienceMode>("match");
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<PlayerId>(
    bestPlayer?.playerId ?? playerRows[0]?.playerId ?? 0,
  );
  const [showDefinitions, setShowDefinitions] = React.useState(false);
  const [evidenceFocus, setEvidenceFocus] =
    React.useState<NutshellEvidenceFocus | null>(null);
  const evidencePanelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!evidenceFocus) return;
    window.setTimeout(() => {
      evidencePanelRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 0);
  }, [evidenceFocus]);
  const edgeSnapshot =
    edgeLeader == null
      ? null
      : teamSnapshots.find((team) => team.team === edgeLeader) ?? null;
  const scoreMargin = Math.abs(props.model.finalScore[0] - props.model.finalScore[1]);
  const marginLabel =
    scoreboardLeader == null
      ? "Even score"
      : scoreMargin <= 2
        ? `${scoreMargin}-point game`
        : `${scoreMargin}-point margin`;
  const closenessLine =
    scoreboardLeader == null
      ? "Treat this like a tiny-edge match: one or two replayable moments can swing the story."
      : scoreMargin <= 2
        ? "Close match. The useful read is small repeatable edges, not a declaration that one side dominated."
        : scoreMargin <= 4
          ? "A few repeated patterns probably explain more than any single rally."
          : "Start with the repeated patterns, then use clips to separate real habits from one-off misses.";
  const shotReadLine =
    edgeLeader == null
      ? "The shot-by-shot read sees the teams as roughly even."
      : edgeLeader === scoreboardLeader
        ? `The shot-by-shot read agrees with the scoreboard: Team ${edgeLeader + 1} had the cleaner rally profile (${fmtSigned(edgeSnapshot?.total ?? 0)}).`
        : `The shot-by-shot read leans Team ${edgeLeader + 1} (${fmtSigned(edgeSnapshot?.total ?? 0)}), so the table below is worth checking for noisy tags or hidden value.`;
  const bestPlayerArea = bestPlayer
    ? areaByValue(bestPlayer.scoringRaw, bestPlayer.stoppingRaw, "best")
    : null;
  const reviewPlayerArea = reviewPlayer
    ? areaByValue(reviewPlayer.scoringRaw, reviewPlayer.stoppingRaw, "review")
    : null;
  const selectedPlayer =
    playerRows.find((row) => row.playerId === selectedPlayerId) ??
    playerRows[0] ??
    null;
  const selectedPlayerBestArea = selectedPlayer
    ? areaByValue(selectedPlayer.scoringRaw, selectedPlayer.stoppingRaw, "best")
    : null;
  const selectedPlayerReviewArea = selectedPlayer
    ? areaByValue(selectedPlayer.scoringRaw, selectedPlayer.stoppingRaw, "review")
    : null;
  const bestArea = areaText(bestPlayerArea);
  const reviewArea = areaText(reviewPlayerArea);
  const selectPlayerAndOpenBestArea = (playerId: PlayerId) => {
    setSelectedPlayerId(playerId);
    setAudienceMode("player");
    const row = playerRows.find((candidate) => candidate.playerId === playerId) ?? null;
    const area = row
      ? areaByValue(row.scoringRaw, row.stoppingRaw, "best")
      : null;
    setEvidenceFocus(
      area
        ? {
            owner: "player",
            playerId,
            kind: area.kind,
            role: area.role,
          }
        : null,
    );
  };

  return (
    <Section title="This Match In A Nutshell">
      <Box sx={{ display: "grid", gap: 1.25 }}>
        <AudienceLensHeader
          mode={audienceMode}
          playerRows={playerRows}
          selectedPlayerId={selectedPlayer?.playerId ?? null}
          playerBadge={props.playerBadge}
          playerText={props.playerText}
          onModeChange={setAudienceMode}
          onSelectPlayer={selectPlayerAndOpenBestArea}
        />

        {audienceMode === "player" && selectedPlayer ? (
          <PlayerFirstRead
            row={selectedPlayer}
            bestArea={selectedPlayerBestArea}
            reviewArea={selectedPlayerReviewArea}
            maxAbs={maxAbs}
            playerBadge={props.playerBadge}
            playerText={props.playerText}
            team={props.teamForPlayer(selectedPlayer.playerId)}
            onShowEvidence={setEvidenceFocus}
          />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.15fr 1fr",
              gap: 1,
            }}
          >
            <Box
              sx={{
                p: 1.2,
                bgcolor: "#fff9bd",
                border: "2px solid rgba(0,0,0,0.22)",
                display: "grid",
                gap: 0.75,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "start" }}>
                <Typography sx={{ color: "#111", fontSize: 21, fontWeight: 950, lineHeight: 1.08 }}>
                  {scoreboardLeader == null
                    ? `Tied ${props.model.finalScore[0]}-${props.model.finalScore[1]}`
                    : `Team ${scoreboardLeader + 1} won ${props.model.finalScore[scoreboardLeader]}-${props.model.finalScore[otherTeam(scoreboardLeader)]}`}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  data-pa-definitions-toggle="true"
                  data-pa-definitions-open={showDefinitions ? "true" : "false"}
                  onClick={() => setShowDefinitions((open) => !open)}
                  sx={{
                    minHeight: 28,
                    flexShrink: 0,
                    color: "#111",
                    borderColor: "rgba(0,0,0,0.35)",
                    fontSize: 11,
                    fontWeight: 900,
                    textTransform: "none",
                    bgcolor: "rgba(255,255,255,0.38)",
                  }}
                >
                  {showDefinitions ? "Hide guide" : "Read guide"}
                </Button>
              </Box>
              <Typography sx={{ color: "#111", fontSize: 14, fontWeight: 900, lineHeight: 1.25 }}>
                {marginLabel}. {closenessLine}
              </Typography>
              <Typography sx={{ color: "#222", fontSize: 12.5, lineHeight: 1.35 }}>
                {shotReadLine}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              <FastReadTile
                label="Best single signal"
                main={
                  bestPlayer
                    ? `${props.playerText(bestPlayer.playerId)} ${fmtSigned(bestPlayer.total)}`
                    : "Not enough data"
                }
                detail={plainAreaText(bestPlayerArea)}
                tone={(bestPlayer?.total ?? 0) >= -0.05 ? "good" : "review"}
              />
              <FastReadTile
                label="Review first"
                main={
                  reviewPlayer
                    ? `${props.playerText(reviewPlayer.playerId)} ${fmtSigned(reviewPlayer.total)}`
                    : "Not enough data"
                }
                detail={plainAreaText(reviewPlayerArea)}
                tone={(reviewPlayer?.total ?? 0) < -0.05 ? "review" : "neutral"}
              />
              <FastReadTile
                label="Best team pattern"
                main={edgeSnapshot ? `Team ${edgeSnapshot.team + 1}` : "Even"}
                detail={
                  edgeSnapshot
                    ? plainAreaText(
                        areaByValue(edgeSnapshot.scoringRaw, edgeSnapshot.stoppingRaw, "best"),
                      )
                    : "no clear edge"
                }
                tone={edgeLeader == null || edgeLeader === scoreboardLeader ? "good" : "neutral"}
              />
              <FastReadTile
                label="Use the clips for"
                main="Proof, not vibes"
                detail="Open examples before turning a number into a practice priority."
                tone="neutral"
              />
            </Box>
          </Box>
        )}

        <Box
          data-pa-definitions-region="true"
          sx={{ display: showDefinitions ? "block" : "none" }}
        >
          <NutshellDefinitions />
        </Box>

        {evidenceFocus ? (
          <Box ref={evidencePanelRef}>
            <NutshellEvidencePanel
              focus={evidenceFocus}
              evidence={nutshellEvidence}
              totalRallies={totalRallies}
              playerBadge={props.playerBadge}
              teamBadge={props.teamBadge}
              playerText={props.playerText}
              onClose={() => setEvidenceFocus(null)}
              onOpenRallyShot={props.onOpenRallyShot}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.05fr 1fr 1fr 1fr",
            gap: 1,
          }}
        >
          <NutshellGlanceCard
            label="Final"
            main={scoreboardLeader == null ? "Tie" : `Team ${scoreboardLeader + 1}`}
            detail={`${props.model.finalScore[0]}-${props.model.finalScore[1]}`}
            positive
          />
          <NutshellGlanceCard
            label="Shot read"
            main={
              edgeLeader == null
                ? "Even"
                : `Team ${edgeLeader + 1} ${fmtSigned(edgeSnapshot?.total ?? 0)}`
            }
            detail="each rally counted once"
            positive={edgeLeader == null || edgeLeader === scoreboardLeader}
          />
          <NutshellGlanceCard
            label="Best"
            main={
              bestPlayer
                ? `${props.playerText(bestPlayer.playerId)} ${fmtSigned(bestPlayer.total)}`
                : "Not enough data"
            }
            detail={bestArea}
            positive={(bestPlayer?.total ?? 0) >= -0.05}
          />
          <NutshellGlanceCard
            label="Review"
            main={
              reviewPlayer
                ? `${props.playerText(reviewPlayer.playerId)} ${fmtSigned(reviewPlayer.total)}`
                : "Not enough data"
            }
            detail={reviewArea}
            positive={(reviewPlayer?.total ?? 0) >= -0.05}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {teamSnapshots.map((team) => (
            <NutshellTeamCard
              key={team.team}
              snapshot={team}
              maxAbs={maxAbs}
              teamBadge={props.teamBadge}
              onShowEvidence={setEvidenceFocus}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {playerRows.map((row) => (
            <NutshellPlayerCard
              key={row.playerId}
              row={row}
              maxAbs={maxAbs}
              playerBadge={props.playerBadge}
              playerText={props.playerText}
              onShowEvidence={setEvidenceFocus}
            />
          ))}
        </Box>

        <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.35 }}>
          Quick-read math: this top section counts each rally once and splits its
          contribution across the players who touched the ball. The detailed
          tables below keep the raw shot-category view, so a single rally can
          appear in several rows there.
        </Typography>
      </Box>
    </Section>
  );
}

function AudienceLensHeader(props: {
  mode: NutshellAudienceMode;
  playerRows: NutshellShareRow[];
  selectedPlayerId: PlayerId | null;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  playerText: (playerId: PlayerId) => string;
  onModeChange: (mode: NutshellAudienceMode) => void;
  onSelectPlayer: (playerId: PlayerId) => void;
}) {
  return (
    <Box
      sx={{
        p: 0.9,
        bgcolor: "#eef8e8",
        border: "1px solid rgba(0,0,0,0.16)",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 1,
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant={props.mode === "match" ? "contained" : "outlined"}
          onClick={() => props.onModeChange("match")}
          sx={{
            minHeight: 30,
            px: 1,
            fontSize: 12,
            fontWeight: 900,
            textTransform: "none",
          }}
        >
          Match story
        </Button>
        <Button
          size="small"
          variant={props.mode === "player" ? "contained" : "outlined"}
          onClick={() => props.onModeChange("player")}
          sx={{
            minHeight: 30,
            px: 1,
            fontSize: 12,
            fontWeight: 900,
            textTransform: "none",
          }}
        >
          Player view
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.55,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <Typography sx={{ color: "#333", fontSize: 11.5, fontWeight: 900, mr: 0.2 }}>
          Player
        </Typography>
        {props.playerRows.map((row) => (
          <Button
            key={row.playerId}
            size="small"
            variant={
              props.mode === "player" && props.selectedPlayerId === row.playerId
                ? "contained"
                : "outlined"
            }
            onClick={() => props.onSelectPlayer(row.playerId)}
            aria-label={`Open best current signal clips for ${props.playerText(row.playerId)}`}
            sx={{
              minWidth: 0,
              minHeight: 32,
              px: 0.55,
              py: 0.15,
              color:
                props.mode === "player" && props.selectedPlayerId === row.playerId
                  ? "#fff"
                  : "#111",
              borderColor: "rgba(0,0,0,0.28)",
              bgcolor:
                props.mode === "player" && props.selectedPlayerId === row.playerId
                  ? undefined
                  : "rgba(255,255,255,0.45)",
              textTransform: "none",
            }}
          >
            {props.playerBadge(row.playerId, 28)}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function PlayerFirstRead(props: {
  row: NutshellShareRow;
  bestArea: NutshellArea | null;
  reviewArea: NutshellArea | null;
  maxAbs: number;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  playerText: (playerId: PlayerId) => string;
  team: Team01;
  onShowEvidence: (focus: NutshellEvidenceFocus) => void;
}) {
  const openKind = (kind: EvidenceKind) => {
    props.onShowEvidence({
      owner: "player",
      playerId: props.row.playerId,
      kind,
    });
  };
  const openArea = (area: NutshellArea | null) => {
    if (!area) return;
    props.onShowEvidence({
      owner: "player",
      playerId: props.row.playerId,
      kind: area.kind,
      role: area.role,
    });
  };
  const reviewPositive =
    props.reviewArea != null && props.reviewArea.value >= -0.05;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.2fr 1fr",
        gap: 1,
      }}
    >
      <Box
        sx={{
          p: 1.15,
          bgcolor: props.row.total >= 0 ? "#fff9bd" : "#fce4d6",
          border: "2px solid rgba(0,0,0,0.22)",
          display: "grid",
          gap: 0.8,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
            {props.playerBadge(props.row.playerId, 42)}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#111", fontSize: 19, fontWeight: 950, lineHeight: 1.08 }}>
                {props.playerText(props.row.playerId)} first read
              </Typography>
              <Typography sx={{ color: "#333", fontSize: 11.5, fontWeight: 800 }}>
                Team {props.team + 1}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ ...paSx(props.row.total), fontSize: 22, flexShrink: 0 }}>
            {fmtSigned(props.row.total)}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gap: 0.45 }}>
          <SignedMeter
            label="Helped scoring"
            value={props.row.scoring}
            maxAbs={props.maxAbs}
          />
          <SignedMeter
            label="Stopped scoring"
            value={props.row.stopping}
            maxAbs={props.maxAbs}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 0.75,
          }}
        >
          <FastReadTile
            label="Best current signal"
            main={areaText(props.bestArea)}
            detail={props.bestArea ? "Highest positive slice in this view." : "Not enough clips yet"}
            tone="good"
            actionLabel="Open best clips"
            onClick={props.bestArea ? () => openArea(props.bestArea) : undefined}
          />
          <FastReadTile
            label="Review candidate"
            main={areaText(props.reviewArea)}
            detail={
              props.reviewArea
                ? reviewPositive
                  ? "Lowest slice, still positive."
                  : "Lowest slice in this view."
                : "Not enough clips yet"
            }
            tone={reviewPositive ? "neutral" : "review"}
            actionLabel="Open review clips"
            onClick={props.reviewArea ? () => openArea(props.reviewArea) : undefined}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          bgcolor: "#eef8e8",
          border: "1px solid rgba(0,0,0,0.16)",
          display: "grid",
          alignContent: "start",
          gap: 0.75,
        }}
      >
        <Typography sx={{ color: "#111", fontSize: 13, fontWeight: 950 }}>
          All player clips
        </Typography>
        <Box sx={{ display: "grid", gap: 0.6 }}>
          <Button
            size="small"
            variant="outlined"
            data-pa-evidence-player={props.row.playerId}
            data-pa-evidence-kind="created"
            onClick={() => openKind("created")}
            sx={playerLensButtonSx}
          >
            Scoring clips
          </Button>
          <Button
            size="small"
            variant="outlined"
            data-pa-evidence-player={props.row.playerId}
            data-pa-evidence-kind="prevented"
            onClick={() => openKind("prevented")}
            sx={playerLensButtonSx}
          >
            Stopping clips
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

const playerLensButtonSx = {
  minHeight: 34,
  color: "#111",
  borderColor: "rgba(0,0,0,0.28)",
  bgcolor: "rgba(255,255,255,0.38)",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "none",
};

function NutshellGlanceCard(props: {
  label: string;
  main: string;
  detail: string;
  positive?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 0.9,
        minHeight: 76,
        bgcolor: props.positive === false ? "#fce4d6" : "#f7f7cf",
        border: "1px solid rgba(0,0,0,0.12)",
        display: "grid",
        alignContent: "center",
        gap: 0.2,
      }}
    >
      <Typography sx={{ color: "#333", fontSize: 10.5, fontWeight: 900, textTransform: "uppercase" }}>
        {props.label}
      </Typography>
      <Typography sx={{ color: "#111", fontSize: 20, fontWeight: 950, lineHeight: 1.1 }}>
        {props.main}
      </Typography>
      <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.25 }}>
        {props.detail}
      </Typography>
    </Box>
  );
}

function FastReadTile(props: {
  label: string;
  main: string;
  detail: string;
  tone: "good" | "review" | "neutral";
  actionLabel?: string;
  onClick?: () => void;
}) {
  const toneSx =
    props.tone === "review"
      ? { bgcolor: "#fce4d6", borderColor: "rgba(204,0,0,0.28)" }
      : props.tone === "good"
        ? { bgcolor: "#f7f7cf", borderColor: "rgba(78,122,35,0.32)" }
        : { bgcolor: "#eef8e8", borderColor: "rgba(0,0,0,0.14)" };
  const content = (
    <>
      <Typography sx={{ color: "#333", fontSize: 10.5, fontWeight: 950, textTransform: "uppercase" }}>
        {props.label}
      </Typography>
      <Typography sx={{ color: "#111", fontSize: 18, fontWeight: 950, lineHeight: 1.08 }}>
        {props.main}
      </Typography>
      <Typography sx={{ color: "#222", fontSize: 11.5, lineHeight: 1.25 }}>
        {props.detail}
      </Typography>
      {props.onClick && props.actionLabel ? (
        <Box
          component="span"
          sx={{
            mt: 0.2,
            justifySelf: "start",
            px: 0.65,
            py: 0.22,
            border: "1px solid rgba(0,0,0,0.32)",
            bgcolor: "rgba(255,255,255,0.55)",
            color: "#111",
            fontSize: 10.5,
            fontWeight: 950,
            lineHeight: 1.15,
          }}
        >
          {props.actionLabel}
        </Box>
      ) : null}
    </>
  );

  if (props.onClick) {
    return (
      <Button
        type="button"
        variant="text"
        onClick={props.onClick}
        sx={{
          p: 0.9,
          minHeight: 92,
          border: "1px solid",
          display: "grid",
          alignContent: "start",
          justifyContent: "stretch",
          gap: 0.35,
          textAlign: "left",
          textTransform: "none",
          borderRadius: 0,
          color: "#111",
          ...toneSx,
          "&:hover": {
            ...toneSx,
            filter: "brightness(0.97)",
            borderColor: "rgba(0,112,192,0.7)",
          },
        }}
      >
        {content}
      </Button>
    );
  }

  return (
    <Box
      sx={{
        p: 0.9,
        minHeight: 92,
        border: "1px solid",
        display: "grid",
        alignContent: "start",
        gap: 0.35,
        ...toneSx,
      }}
    >
      {content}
    </Box>
  );
}

function NutshellDefinitions() {
  const rows = [
    {
      label: "Overall",
      body:
        "A quick scouting read, not a scoreboard total. Positive means the team/player helped tilt rallies their way; negative means those rallies tilted away.",
    },
    {
      label: "Helped scoring",
      body:
        "The part of the read from rallies where this side served. Each rally contribution is split across the server and later serving-team shots.",
    },
    {
      label: "Stopped scoring",
      body:
        "The part of the read from rallies where this side received. Each rally contribution is split across returns and later receiving-team shots.",
    },
    {
      label: "Best area",
      body:
        "The shot family where the estimate moved most in a helpful direction.",
    },
    {
      label: "Review area",
      body:
        "The shot family that most deserves video review. It may still be positive if the player had a clean match.",
    },
    {
      label: "Why this differs from the tables",
      body:
        "The nutshell avoids double-counting by counting each rally once. The worksheet tables below stay closer to the source PDF and intentionally show raw shot-category rows.",
    },
  ];

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: "#eef8e8",
        border: "1px solid rgba(0,0,0,0.14)",
        display: "grid",
        gap: 0.75,
      }}
    >
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: "grid",
            gridTemplateColumns: "260px 1fr",
            gap: 1,
            alignItems: "start",
          }}
        >
          <Typography sx={{ color: "#111", fontSize: 12, fontWeight: 900 }}>
            {row.label}
          </Typography>
          <Typography sx={{ color: "#111", fontSize: 12, lineHeight: 1.35 }}>
            {row.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function NutshellTeamCard(props: {
  snapshot: NutshellTeamSnapshot;
  maxAbs: number;
  teamBadge: (team: Team01) => React.ReactNode;
  onShowEvidence: (focus: NutshellEvidenceFocus) => void;
}) {
  const { snapshot } = props;
  const bestScoring = roleByValue(snapshot.scoringRaw, "best");
  const bestStopping = roleByValue(snapshot.stoppingRaw, "best");
  const reviewScoring = roleByValue(snapshot.scoringRaw, "review");
  const reviewStopping = roleByValue(snapshot.stoppingRaw, "review");
  return (
    <Box
      sx={{
        p: 1,
        bgcolor: snapshot.total >= 0 ? "#f7f7cf" : "#fce4d6",
        border: "1px solid rgba(0,0,0,0.12)",
        display: "grid",
        gap: 0.65,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>{props.teamBadge(snapshot.team)}</Box>
        <Box sx={{ color: "#111", fontWeight: 900 }}>Score {snapshot.score}</Box>
      </Box>
      <SignedMeter label="Overall" value={snapshot.total} maxAbs={props.maxAbs} />
      <SignedMeter label="Helped scoring" value={snapshot.scoring} maxAbs={props.maxAbs} />
      <SignedMeter label="Stopped scoring" value={snapshot.stopping} maxAbs={props.maxAbs} />
      <Box sx={{ display: "grid", gap: 0.35 }}>
        <NutshellCalcDetails
          title={`Team ${snapshot.team + 1} · ${calcKindLabel("overall")}`}
          raw={combinedRoleShares(snapshot.scoringRaw, snapshot.stoppingRaw)}
          value={snapshot.total}
          owner={{ owner: "team", team: snapshot.team }}
          onShowEvidence={props.onShowEvidence}
        />
        <NutshellCalcDetails
          title={`Team ${snapshot.team + 1} · ${calcKindLabel("scoring")}`}
          raw={snapshot.scoringRaw}
          value={snapshot.scoring}
          owner={{ owner: "team", team: snapshot.team }}
          onShowEvidence={props.onShowEvidence}
        />
        <NutshellCalcDetails
          title={`Team ${snapshot.team + 1} · ${calcKindLabel("stopping")}`}
          raw={snapshot.stoppingRaw}
          value={snapshot.stopping}
          owner={{ owner: "team", team: snapshot.team }}
          onShowEvidence={props.onShowEvidence}
        />
      </Box>
      <Typography sx={{ color: "#111", fontSize: 11.5, lineHeight: 1.35 }}>
        Best areas: {roleValueText(snapshot.scoringRaw, bestScoring)}
        {" · "} {roleValueText(snapshot.stoppingRaw, bestStopping)}
      </Typography>
      <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.35 }}>
        Review areas: {roleValueText(snapshot.scoringRaw, reviewScoring)}
        {" · "} {roleValueText(snapshot.stoppingRaw, reviewStopping)}
      </Typography>
    </Box>
  );
}

function NutshellPlayerCard(props: {
  row: NutshellShareRow;
  maxAbs: number;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  playerText: (playerId: PlayerId) => string;
  onShowEvidence: (focus: NutshellEvidenceFocus) => void;
}) {
  const bestLabel = areaText(
    areaByValue(props.row.scoringRaw, props.row.stoppingRaw, "best"),
  );
  const reviewLabel = areaText(
    areaByValue(props.row.scoringRaw, props.row.stoppingRaw, "review"),
  );
  return (
    <Box
      sx={{
        p: 0.85,
        bgcolor: props.row.total >= 0 ? "#f7f7cf" : "#fce4d6",
        border: "1px solid rgba(0,0,0,0.12)",
        display: "grid",
        gap: 0.55,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        {props.playerBadge(props.row.playerId, 34)}
        <Box sx={{ ...paSx(props.row.total), fontSize: 13 }}>
          {fmtSigned(props.row.total)}
        </Box>
      </Box>
      <SignedMeter label="Helped scoring" value={props.row.scoring} maxAbs={props.maxAbs} compact />
      <SignedMeter label="Stopped scoring" value={props.row.stopping} maxAbs={props.maxAbs} compact />
      <Box sx={{ display: "grid", gap: 0.35 }}>
        <NutshellCalcDetails
          title={`${props.playerText(props.row.playerId)} · ${calcKindLabel("overall")}`}
          raw={combinedRoleShares(props.row.scoringRaw, props.row.stoppingRaw)}
          value={props.row.total}
          owner={{ owner: "player", playerId: props.row.playerId }}
          onShowEvidence={props.onShowEvidence}
        />
        <NutshellCalcDetails
          title={`${props.playerText(props.row.playerId)} · ${calcKindLabel("scoring")}`}
          raw={props.row.scoringRaw}
          value={props.row.scoring}
          owner={{ owner: "player", playerId: props.row.playerId }}
          onShowEvidence={props.onShowEvidence}
        />
        <NutshellCalcDetails
          title={`${props.playerText(props.row.playerId)} · ${calcKindLabel("stopping")}`}
          raw={props.row.stoppingRaw}
          value={props.row.stopping}
          owner={{ owner: "player", playerId: props.row.playerId }}
          onShowEvidence={props.onShowEvidence}
        />
      </Box>
      <Typography sx={{ color: "#111", fontSize: 11.5, lineHeight: 1.3 }}>
        Best area: {bestLabel}
      </Typography>
      <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.3 }}>
        Review area: {reviewLabel}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          data-pa-evidence-player={props.row.playerId}
          data-pa-evidence-kind="created"
          onClick={() =>
            props.onShowEvidence({
              owner: "player",
              playerId: props.row.playerId,
              kind: "created",
            })
          }
          sx={nutshellEvidenceButtonSx}
        >
          Scoring clips
        </Button>
        <Button
          size="small"
          variant="outlined"
          data-pa-evidence-player={props.row.playerId}
          data-pa-evidence-kind="prevented"
          onClick={() =>
            props.onShowEvidence({
              owner: "player",
              playerId: props.row.playerId,
              kind: "prevented",
            })
          }
          sx={nutshellEvidenceButtonSx}
        >
          Stopping clips
        </Button>
      </Box>
    </Box>
  );
}

const nutshellEvidenceButtonSx = {
  minHeight: 24,
  px: 0.7,
  py: 0.15,
  color: "#111",
  borderColor: "rgba(0,0,0,0.28)",
  fontSize: 10.5,
  fontWeight: 900,
  textTransform: "none",
  bgcolor: "rgba(255,255,255,0.35)",
};

function NutshellCalcDetails(props: {
  title: string;
  raw: Record<string, number>;
  value: number;
  owner?: NutshellEvidenceOwner;
  onShowEvidence?: (focus: NutshellEvidenceFocus) => void;
}) {
  return (
    <Box
      component="details"
      sx={{
        bgcolor: "rgba(255,255,255,0.25)",
        border: "1px solid rgba(0,0,0,0.10)",
        "&[open]": { bgcolor: "rgba(255,255,255,0.45)" },
      }}
    >
      <Box
        component="summary"
        sx={{
          cursor: "pointer",
          px: 0.65,
          py: 0.35,
          color: "#111",
          fontSize: 10.8,
          fontWeight: 900,
          lineHeight: 1.2,
          listStylePosition: "inside",
        }}
      >
        {props.title}: <Box component="span" sx={paSx(props.value)}>{fmtSigned(props.value)}</Box>
      </Box>
      <Box sx={{ p: 0.45, pt: 0 }}>
        <NutshellCalcPanel
          raw={props.raw}
          value={props.value}
          owner={props.owner}
          onShowEvidence={props.onShowEvidence}
        />
      </Box>
    </Box>
  );
}

function NutshellCalcPanel(props: {
  raw: Record<string, number>;
  value: number;
  owner?: NutshellEvidenceOwner;
  onShowEvidence?: (focus: NutshellEvidenceFocus) => void;
}) {
  const rows = rawShareRows(props.raw);
  const rawTotal = rawShareTotal(props.raw);
  const remainder = props.value - rawTotal;
  const showRemainder = Math.abs(remainder) > 0.05;
  const showClips = Boolean(props.owner && props.onShowEvidence);
  return (
    <Box
      sx={{
        p: 0.75,
        bgcolor: "rgba(255,255,255,0.45)",
        border: "1px solid rgba(0,0,0,0.14)",
        display: "grid",
        gap: 0.45,
      }}
    >
      <Typography sx={{ color: "#333", fontSize: 10.8, lineHeight: 1.3 }}>
        These rows are the visible shot-role chunks behind the card. The final
        value is their sum. If a small extra line appears, it means the score
        result for a rally was known, but PBV did not tag enough touches in that
        rally to attach every last bit to a visible shot role.
      </Typography>
      <Box component="table" sx={{ ...tableSx(128), tableLayout: "auto" }}>
        <tbody>
          {rows.map(([role, value]) => {
            const kind = roleEvidenceKind(role);
            const openEvidence = (event: React.MouseEvent) => {
              event.stopPropagation();
              if (!props.owner || !props.onShowEvidence) return;
              props.onShowEvidence({ ...props.owner, kind, role });
            };
            return (
              <tr key={role}>
                <td>{roleSummaryLabel(role)}</td>
                <td>
                  <PointsAddedCell value={value} />
                </td>
                {showClips ? (
                  <td>
                    <Button
                      size="small"
                      variant="outlined"
                      data-pa-nutshell-role={role}
                      data-pa-nutshell-kind={kind}
                      onClick={openEvidence}
                      sx={{
                        ...nutshellEvidenceButtonSx,
                        minHeight: 20,
                        px: 0.45,
                        fontSize: 9.5,
                      }}
                    >
                      Clips
                    </Button>
                  </td>
                ) : null}
              </tr>
            );
          })}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={showClips ? 3 : 2}>No tagged contributors for this slice.</td>
            </tr>
          ) : null}
          <tr>
            <td>
              <b>Visible chunks</b>
            </td>
            <td>
              <PointsAddedCell value={rawTotal} />
            </td>
            {showClips ? <td /> : null}
          </tr>
          {showRemainder ? (
            <tr>
              <td>Not tied to a visible shot role</td>
              <td>
                <PointsAddedCell value={remainder} />
              </td>
              {showClips ? <td /> : null}
            </tr>
          ) : null}
          <tr>
            <td>
              <b>Final card value</b>
            </td>
            <td>
              <PointsAddedCell value={props.value} />
            </td>
            {showClips ? <td /> : null}
          </tr>
        </tbody>
      </Box>
    </Box>
  );
}

function evidenceKindLabel(kind: EvidenceKind): string {
  return kind === "created" ? "Helped scoring" : "Stopped scoring";
}

function evidenceOutcomeText(item: PointsAddedEvidenceItem): string {
  if (item.kind === "created") {
    if (item.failed) return "missed shot; no point";
    if (item.scored) return "team scored";
    if (item.goal) return "reached kitchen";
    return "no point";
  }
  if (item.failed) return "missed shot; opponent scored";
  if (item.scored) return "opponent scored";
  if (item.goal) return "opponent reached kitchen";
  return "opponent held";
}

function EvidenceContributionChartTooltip(props: {
  active?: boolean;
  payload?: Array<{ payload?: EvidenceContributionDatum | EvidenceRunningTotalDatum }>;
}) {
  if (!props.active) return null;
  const contributionDatum = props.payload
    ?.map((entry) => entry.payload)
    .find(
      (payload): payload is EvidenceContributionDatum =>
        Boolean(payload && "item" in payload),
    );
  const runningDatum = props.payload
    ?.map((entry) => entry.payload)
    .find(
      (payload): payload is EvidenceRunningTotalDatum =>
        Boolean(payload && "runningTotal" in payload),
    );

  if (!contributionDatum?.item) {
    if (!runningDatum) return null;
    return (
      <Box
        sx={{
          bgcolor: "#fffff2",
          border: "1px solid rgba(0,0,0,0.24)",
          boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
          color: "#111",
          maxWidth: 300,
          p: 0.85,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 900, lineHeight: 1.25 }}>
          Running total after rally {runningDatum.x}
        </Typography>
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, mt: 0.35 }}>
          This rally contributed{" "}
          <Box component="span" sx={contributionSx(runningDatum.rallyContribution)}>
            {fmtContributionSigned(runningDatum.rallyContribution)}
          </Box>{" "}
          to this slice.
        </Typography>
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
          Plotted touches in this slice: <b>{runningDatum.plottedTouches}</b>.
        </Typography>
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
          Shots in rally: <b>{runningDatum.rallyShotCount ?? "-"}</b>.
        </Typography>
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
          Running total:{" "}
          <Box component="span" sx={contributionSx(runningDatum.runningTotal)}>
            {fmtContributionSigned(runningDatum.runningTotal)}
          </Box>
        </Typography>
      </Box>
    );
  }

  const item = contributionDatum.item;

  const formula = item.formula;
  const isCreated = formula?.side === "created";
  const firstValue = formula
    ? isCreated
      ? formula.servingPoint
      : formula.baseline
    : null;
  const secondValue = formula
    ? isCreated
      ? formula.baseline
      : formula.servingPoint
    : null;
  const firstLabel = isCreated ? "server result" : "expected chance of winning rally";
  const secondLabel = isCreated ? "expected chance of winning rally" : "server result";
  const sideLabel = isCreated ? "serving side" : "receiving side";
  const touchText = formula
    ? `${formula.divisor} ${isCreated ? "serving-team" : "receiving-team"} tagged ${
        formula.divisor === 1 ? "touch" : "touches"
      }`
    : null;

  return (
    <Box
      sx={{
        bgcolor: "#fffff2",
        border: "1px solid rgba(0,0,0,0.24)",
        boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
        color: "#111",
        maxWidth: 330,
        p: 0.85,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 900, lineHeight: 1.25 }}>
        Rally {item.row.rallyIndex + 1} · Shot {item.shotNumber} ·{" "}
        {roleLabel(item.role)}
      </Typography>
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, mt: 0.35 }}>
        Outcome: {evidenceOutcomeText(item)}
      </Typography>
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
        Shots in rally: <b>{rowShotCount(item.row)}</b>.
      </Typography>
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.35 }}>
        Contribution to total:{" "}
        <Box component="span" sx={contributionSx(item.contribution)}>
          {fmtContributionSigned(item.contribution)}
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
        This dot is one tagged touch for this player/filter. A broad scoring or
        stopping view can show multiple shot-number dots from the same rally.
      </Typography>
      {formula && firstValue != null && secondValue != null ? (
        <Box sx={{ mt: 0.5, display: "grid", gap: 0.2 }}>
          <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
            Server result:{" "}
            {formula.servingPoint > 0.5 ? "server scored" : "server did not score"} (
            {fmtEquationNumber(formula.servingPoint)}).
          </Typography>
          <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
            Expected chance of winning rally: {pct(formula.baseline)}
            {formula.baselineServeAttempts > 0
              ? ` (${formula.baselineServingPoints} of ${formula.baselineServeAttempts} service rallies).`
              : "."}
          </Typography>
          <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
            Rally value for the {sideLabel}: {fmtEquationNumber(firstValue)} -{" "}
            {fmtEquationNumber(secondValue)} = {fmtEquationSigned(formula.delta)}.
          </Typography>
          <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
            This contribution is split across {touchText}.
          </Typography>
          <Typography sx={{ fontSize: 11.2, lineHeight: 1.35 }}>
            Touches sharing this contribution: <b>{formula.divisor}</b>.
          </Typography>
          <Typography sx={{ fontSize: 11.4, fontWeight: 900, lineHeight: 1.35 }}>
            ({firstLabel} - {secondLabel}) / touches ={" "}
            {fmtContributionSigned(formula.share)}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function EvidenceContributionChart(props: {
  items: PointsAddedEvidenceItem[];
  contextLabel: string;
  totalRallies: number;
  totalContribution: number;
  onOpenItem: (item: PointsAddedEvidenceItem) => void;
}) {
  const data = React.useMemo<EvidenceContributionDatum[]>(
    () =>
      props.items
        .map((item) => ({
          x: item.row.rallyIndex + 1,
          y: item.contribution,
          item,
        }))
        .sort((a, b) => a.x - b.x || a.item.shotNumber - b.item.shotNumber),
    [props.items],
  );
  if (!data.length) return null;

  const shotNumbersInView = Array.from(
    new Set(data.map((datum) => datum.item.shotNumber)),
  ).sort((a, b) => a - b);
  const showShotLabels = shotNumbersInView.length > 1;
  const chartExplanation = showShotLabels
    ? `Dots are individual tagged touches; the number inside each dot is the shot number. A rally can show more than one dot here when this slice includes several shot roles.`
    : `Dots are individual ${roleLabel(data[0].item.role).toLowerCase()} contributions; click a dot to watch the shot.`;

  const xMax = Math.max(
    1,
    props.totalRallies,
    ...data.map((datum) => datum.x),
  );
  const runningData = (() => {
    const contributionByRally = new Map<number, number>();
    const touchesByRally = new Map<number, number>();
    const shotCountByRally = new Map<number, number>();
    for (const datum of data) {
      contributionByRally.set(
        datum.x,
        (contributionByRally.get(datum.x) ?? 0) + datum.y,
      );
      touchesByRally.set(datum.x, (touchesByRally.get(datum.x) ?? 0) + 1);
      if (!shotCountByRally.has(datum.x)) {
        shotCountByRally.set(datum.x, rowShotCount(datum.item.row));
      }
    }
    let runningTotal = 0;
    const rows: EvidenceRunningTotalDatum[] = [];
    for (let rallyNumber = 1; rallyNumber <= xMax; rallyNumber += 1) {
      const rallyContribution = contributionByRally.get(rallyNumber) ?? 0;
      runningTotal += rallyContribution;
      rows.push({
        x: rallyNumber,
        y: runningTotal,
        rallyContribution,
        runningTotal,
        plottedTouches: touchesByRally.get(rallyNumber) ?? 0,
        rallyShotCount: shotCountByRally.get(rallyNumber) ?? null,
      });
    }
    return rows;
  })();
  const maxAbs = Math.max(
    0.1,
    ...data.map((datum) => Math.abs(datum.y)),
    ...runningData.map((datum) => Math.abs(datum.runningTotal)),
  );
  const yMax = Math.ceil(maxAbs * 100) / 100;

  return (
    <Box
      sx={{
        bgcolor: "rgba(255,255,255,0.34)",
        border: "1px solid rgba(0,0,0,0.12)",
        p: 0.8,
      }}
    >
      <Typography sx={{ color: "#111", fontSize: 12.5, fontWeight: 900, mb: 0.4 }}>
        Contributions by rally for {props.contextLabel} · total{" "}
        <Box component="span" sx={contributionSx(props.totalContribution)}>
          {fmtContributionSigned(props.totalContribution)}
        </Box>
      </Typography>
      <Typography sx={{ color: "#333", fontSize: 10.8, lineHeight: 1.25, mb: 0.35 }}>
        {chartExplanation} The light blue line is the running total after each rally.
      </Typography>
      <Box sx={{ height: 155, minWidth: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 12, bottom: 18, left: 0 }}>
            <CartesianGrid stroke="rgba(0,0,0,0.12)" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[1, xMax]}
              allowDecimals={false}
              tick={{ fill: "#222", fontSize: 10 }}
              tickLine={{ stroke: "rgba(0,0,0,0.35)" }}
              label={{
                value: "rally number",
                position: "insideBottom",
                offset: -9,
                fill: "#222",
                fontSize: 10,
              }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[-yMax, yMax]}
              width={44}
              tick={{ fill: "#222", fontSize: 10 }}
              tickFormatter={(value: number | string) =>
                typeof value === "number" ? fmtContributionNumber(value) : String(value)
              }
            />
            <ReferenceLine y={0} stroke="rgba(0,0,0,0.55)" />
            <RechartsTooltip
              cursor={{ stroke: "rgba(0,0,0,0.35)", strokeDasharray: "3 3" }}
              content={<EvidenceContributionChartTooltip />}
            />
            <Scatter
              name="running total"
              data={runningData}
              line={{
                stroke: "#2f6f9d",
                strokeWidth: 2,
                strokeOpacity: 0.45,
              }}
              fill="#2f6f9d"
              fillOpacity={0.18}
              isAnimationActive={false}
            />
            <Scatter
              data={data}
              isAnimationActive={false}
              shape={(shapeProps: any) => {
                const datum = shapeProps.payload as EvidenceContributionDatum | undefined;
                const item = datum?.item;
                const fill = datum && datum.y >= 0 ? "#4f8f2f" : "#e65338";
                return (
                  <g
                    onClick={(event) => {
                      event.stopPropagation();
                      if (item) props.onOpenItem(item);
                    }}
                    style={{ cursor: item ? "pointer" : "default" }}
                  >
                    <circle
                      cx={shapeProps.cx}
                      cy={shapeProps.cy}
                      r={showShotLabels ? 6 : 5}
                      fill={fill}
                      stroke="#111"
                      strokeWidth={0.8}
                    />
                    {showShotLabels && item ? (
                      <text
                        x={shapeProps.cx}
                        y={shapeProps.cy + 2.5}
                        textAnchor="middle"
                        fontSize={7}
                        fontWeight={900}
                        fill="#fff"
                        pointerEvents="none"
                      >
                        {item.shotNumber}
                      </text>
                    ) : null}
                  </g>
                );
              }}
            >
              {data.map((datum) => (
                <Cell
                  key={datum.item.id}
                  fill={datum.y >= 0 ? "#4f8f2f" : "#e65338"}
                  stroke="#111"
                  strokeWidth={0.6}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function NutshellEvidencePanel(props: {
  focus: NutshellEvidenceFocus;
  evidence: PointsAddedEvidenceItem[];
  totalRallies: number;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  teamBadge: (team: Team01) => React.ReactNode;
  playerText: (playerId: PlayerId) => string;
  onClose: () => void;
  onOpenRallyShot: (payload: OpenShotPreviewPayload) => void;
}) {
  const items = props.evidence
    .filter(
      (item) => {
        if (item.kind !== props.focus.kind) return false;
        if (props.focus.role && item.role !== props.focus.role) return false;
        if (isNutshellFocusForPlayer(props.focus)) {
          return item.playerId === props.focus.playerId;
        }
        return item.team === props.focus.team;
      },
    )
    .sort((a, b) => {
      const aPositive = a.contribution > 0.05 ? 0 : 1;
      const bPositive = b.contribution > 0.05 ? 0 : 1;
      if (aPositive !== bPositive) return aPositive - bPositive;
      const roleDelta = roleRank(a.role) - roleRank(b.role);
      if (roleDelta !== 0) return roleDelta;
      return a.row.rallyIndex - b.row.rallyIndex || a.shotNumber - b.shotNumber;
    });
  const shareTotal = items.reduce((sum, item) => sum + item.contribution, 0);
  const matchExpectationFormula =
    items.find((item) => item.formula)?.formula ?? null;
  const positiveCount = items.filter((item) => item.contribution > 0.05).length;
  const negativeCount = items.filter((item) => item.contribution < -0.05).length;
  const ownerNode = isNutshellFocusForPlayer(props.focus)
    ? props.playerBadge(props.focus.playerId, 34)
    : props.teamBadge(props.focus.team);
  const ownerText = isNutshellFocusForPlayer(props.focus)
    ? props.playerText(props.focus.playerId)
    : `Team ${props.focus.team + 1}`;
  const scopeLabel = props.focus.role
    ? roleSummaryLabel(props.focus.role)
    : evidenceKindLabel(props.focus.kind);
  const scopeDetail = props.focus.role
    ? `${ownerText} · ${roleSummaryLabel(props.focus.role)} ${fmtContributionSigned(shareTotal)}`
    : `${ownerText} · ${evidenceKindLabel(props.focus.kind)} ${fmtContributionSigned(shareTotal)}`;

  const openItem = (item: PointsAddedEvidenceItem) => {
    const timing = shotLoopForRow(
      item.row,
      item.shotNumber,
      terminalMissedShotNumber(item.row),
    );
    const label = evidenceKindLabel(item.kind);
    props.onOpenRallyShot({
      row: item.row,
      shotNumber: item.shotNumber,
      title: `Rally ${item.row.rallyIndex + 1} · Shot ${item.shotNumber}`,
      context: `${roleLabel(item.role)} by ${props.playerText(
        item.playerId,
      )} · ${label} ${fmtContributionSigned(item.contribution)} · ${evidenceOutcomeText(
        item,
      )}`,
      timeSec: timing.timeSec,
      loop: timing.loop,
    });
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: "#eef8e8",
        border: "1px solid rgba(0,0,0,0.16)",
        display: "grid",
        gap: 0.8,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {ownerNode}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#111", fontSize: 13, fontWeight: 900 }}>
              Clips for {scopeDetail}
            </Typography>
            <Typography sx={{ color: "#333", fontSize: 11.5, lineHeight: 1.35 }}>
              {items.length} {scopeLabel} clips. These contributions sum to{" "}
              {fmtContributionSigned(shareTotal)} in the top summary.
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={props.onClose}
          sx={nutshellEvidenceButtonSx}
        >
          Hide evidence
        </Button>
      </Box>

      <Typography sx={{ color: "#111", fontSize: 12, lineHeight: 1.35 }}>
        Each row is that shot&apos;s contribution to the expanded total. Green
        rows are examples to copy or reinforce; red rows are review candidates.
        Watch opens the rally already scrubbed around that shot.
      </Typography>

      <MatchExpectationPhilosophy
        baseline={matchExpectationFormula?.baseline ?? null}
        servingPoints={matchExpectationFormula?.baselineServingPoints ?? null}
        serveAttempts={matchExpectationFormula?.baselineServeAttempts ?? null}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", color: "#111", fontSize: 12 }}>
        <Box component="span">
          Helpful examples: <b>{positiveCount}</b>
        </Box>
        <Box component="span">
          Review examples: <b>{negativeCount}</b>
        </Box>
      </Box>

      <EvidenceContributionChart
        items={items}
        contextLabel={`${ownerText} · ${scopeLabel}`}
        totalRallies={props.totalRallies}
        totalContribution={shareTotal}
        onOpenItem={openItem}
      />

      <Box sx={{ maxHeight: 260, overflow: "auto", border: "1px solid rgba(0,0,0,0.08)" }}>
        <Box component="table" sx={{ ...tableSx(82), tableLayout: "auto" }}>
          <thead>
            <tr>
              <th>Rally</th>
              <th>Rally shots</th>
              <th>Shot</th>
              <th>Role</th>
              <th>Score</th>
              <th>Outcome</th>
              <th>Contribution to total</th>
              <th>Video</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <Box component="tr" key={item.id} sx={rowToneSx(item.contribution)}>
                <td>{item.row.rallyIndex + 1}</td>
                <td>{rowShotCount(item.row)}</td>
                <td>{item.shotNumber}</td>
                <td>{roleLabel(item.role)}</td>
                <td>{scorePairText(item.row.scoreBefore)} → {scorePairText(item.row.scoreAfter)}</td>
                <td>{evidenceOutcomeText(item)}</td>
                <td>
                  <PointsAddedCell value={item.contribution} />
                  <ShareMathDetails item={item} />
                </td>
                <td>
                  <Button
                    size="small"
                    variant="outlined"
                    data-pa-watch-id={item.id}
                    onClick={() => openItem(item)}
                    sx={nutshellEvidenceButtonSx}
                  >
                    Watch
                  </Button>
                </td>
              </Box>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={8}>No evidence rows for this slice yet.</td>
              </tr>
            ) : null}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

function SignedMeter(props: {
  label: string;
  value: number;
  maxAbs: number;
  compact?: boolean;
}) {
  const pctWidth = Math.min(100, (Math.abs(props.value) / Math.max(0.01, props.maxAbs)) * 100);
  const positive = props.value >= 0;
  return (
    <Box sx={{ display: "grid", gap: 0.25 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          color: "#111",
          fontSize: props.compact ? 10.5 : 11.5,
          fontWeight: 800,
        }}
      >
        <Box component="span">{props.label}</Box>
        <Box component="span" sx={paSx(props.value)}>
          {fmtSigned(props.value)}
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          height: props.compact ? 5 : 7,
          bgcolor: "rgba(0,0,0,0.10)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: positive ? "50%" : `${50 - pctWidth / 2}%`,
            width: `${pctWidth / 2}%`,
            bgcolor: positive ? "#7fba4b" : "#e65338",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: 1,
            bgcolor: "rgba(0,0,0,0.38)",
          }}
        />
      </Box>
    </Box>
  );
}

function ScoringMathTable() {
  return (
    <Section title="Scoring Math">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 520px) 1fr",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box component="table" sx={{ ...tableSx(), tableLayout: "auto" }}>
          <thead>
            <tr>
              <th>Difference</th>
              <th>Win %</th>
              <th>Point Chg</th>
              <th>Best of 3%</th>
            </tr>
          </thead>
          <tbody>
            {PROBABILITY_ROWS.map(([difference, win, pointChange, bestOf3]) => (
              <tr key={difference}>
                <td>{difference.toFixed(2)}</td>
                <td>{win}</td>
                <td>{pointChange}</td>
                <td>{bestOf3}</td>
              </tr>
            ))}
          </tbody>
        </Box>
        <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: "#111" }}>
          This is a small reference table copied from the scoring-math PDF, not
          a PBV-derived calculation. It is a quick scale for interpreting how a
          small scoring-rate edge can change expected points and match odds. The
          live PickleOdds simulator is a better place for interactive what-if
          work:{" "}
          <Box
            component="a"
            href="https://learnforeverlearn.com/pickleodds/"
            target="_blank"
            rel="noreferrer"
            sx={{ color: "#0070c0", fontWeight: 900 }}
          >
            open PickleOdds
          </Box>
          .
        </Typography>
      </Box>
    </Section>
  );
}

function roleName(role: Role): string {
  return roleSummaryLabel(role);
}

function bestRole(raw: Record<string, number>): { role: Role; value: number } {
  return (Object.keys(raw) as Role[])
    .map((role) => ({ role, value: raw[role] }))
    .sort((a, b) => b.value - a.value)[0] ?? {
      role: roleFromShotNumber(1),
      value: 0,
    };
}

function worstRole(raw: Record<string, number>): { role: Role; value: number } {
  return (Object.keys(raw) as Role[])
    .map((role) => ({ role, value: raw[role] }))
    .sort((a, b) => a.value - b.value)[0] ?? {
      role: roleFromShotNumber(1),
      value: 0,
    };
}

function playerKeyFromComboKey(key: string): [PlayerId | null, PlayerId | null] {
  const parts = key.split(":").map((part) => Number(part));
  const [a, b] = parts.length >= 4 ? parts.slice(-2) : parts;
  return [asPlayerId(a), asPlayerId(b)];
}

function TextBlock(props: { children: React.ReactNode; tone?: "report" }) {
  return (
    <Typography
      component="p"
      sx={{
        m: 0,
        color: "#111",
        fontSize: 14,
        lineHeight: 1.55,
      }}
    >
      {props.children}
    </Typography>
  );
}

function TextList(props: { items: React.ReactNode[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.2, display: "grid", gap: 0.55 }}>
      {props.items.map((item, index) => (
        <Box
          component="li"
          key={index}
          sx={{
            color: "#111",
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          {item}
        </Box>
      ))}
    </Box>
  );
}

function TextReport(props: {
  model: PointsAddedModel;
  rows: KitchenArrivalRow[];
  playerText: (playerId: PlayerId) => string;
  teamText: (team: Team01) => string;
  reportTone?: boolean;
}) {
  const { model, rows, playerText, teamText } = props;
  const sortedPlayers = [...model.summary].sort((a, b) => b.total - a.total);
  const topPlayer = sortedPlayers[0] ?? null;
  const lowPlayer = sortedPlayers[sortedPlayers.length - 1] ?? null;
  const topOffense = [...model.summary].sort((a, b) => b.offense - a.offense)[0] ?? null;
  const topDefense = [...model.summary].sort((a, b) => b.defense - a.defense)[0] ?? null;
  const allCombos = [
    ...model.allOffensivePairs.map((row) => ({ ...row, label: "serving-team pair" })),
    ...model.allDefensivePairs.map((row) => ({ ...row, label: "receiving-team pair" })),
  ];
  const meaningfulCombos = allCombos.filter((row) => row.attempts >= 2);
  const bestCombo = [...meaningfulCombos].sort(
    (a, b) => b.pointsAdded - a.pointsAdded,
  )[0];
  const worstCombo = [...meaningfulCombos].sort(
    (a, b) => a.pointsAdded - b.pointsAdded,
  )[0];
  const kitchenLeader = [...model.kitchen].sort(
    (a, b) => b.pointsAdded - a.pointsAdded,
  )[0];

  const comboText = (combo: (ComboRow & { label: string }) | undefined) => {
    if (!combo) return "No repeated combination has enough attempts yet.";
    const [a, b] = playerKeyFromComboKey(combo.key);
    const first = a == null ? "unknown" : playerText(a);
    const second = b == null ? "unknown" : playerText(b);
    return `${first} to ${second} on ${combo.label}: ${fmtNumber(
      combo.pointsAdded,
    )} points added across ${combo.attempts} attempts (${pct(combo.average)}).`;
  };

  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Section title="Summary">
        <Box sx={{ display: "grid", gap: 0.8 }}>
          <TextBlock>
            This is a first-pass text report generated from PBV rally and shot
            data. The comparison rate is this game&apos;s observed serving-team scoring average
            of {pct(model.scoringRate)} across {model.serveAttemptRows.length} serve
            attempts.
          </TextBlock>
          <TextList
            items={[
              topPlayer
                ? `${playerText(topPlayer.playerId)} leads the current Points Added estimate at ${fmtNumber(
                    topPlayer.total,
                  )}.`
                : "No player summary is available yet.",
              lowPlayer
                ? `${playerText(lowPlayer.playerId)} has the lowest current estimate at ${fmtNumber(
                    lowPlayer.total,
                  )}.`
                : "No low-end player summary is available yet.",
              topOffense
                ? `${playerText(topOffense.playerId)} has the strongest offensive estimate (${fmtNumber(
                    topOffense.offense,
                  )}).`
                : "No offensive split is available yet.",
              topDefense
                ? `${playerText(topDefense.playerId)} has the strongest defensive estimate (${fmtNumber(
                    topDefense.defense,
                  )}).`
                : "No defensive split is available yet.",
            ]}
          />
        </Box>
      </Section>

      <Section title="Player Notes">
        <TextList
          items={[...model.summary].sort((a, b) => a.sortOrder - b.sortOrder).map((row) => {
            const best = bestRole(row.raw);
            const worst = worstRole(row.raw);
            return `${playerText(row.playerId)}: ${fmtNumber(
              row.total,
            )} average points added. Best area is ${roleName(best.role)} (${fmtNumber(
              best.value,
            )}); biggest drag is ${roleName(worst.role)} (${fmtNumber(
              worst.value,
            )}). Offense ${fmtNumber(row.offense)}, defense ${fmtNumber(row.defense)}.`;
          })}
        />
      </Section>

      <Section title="Combinations">
        <TextList
          items={[
            comboText(bestCombo),
            comboText(worstCombo),
            "Combination lines are filtered to repeated pairings so one-off weird rallies do not dominate the writeup.",
          ]}
        />
      </Section>

      <Section title="Kitchen">
        <TextList
          items={[
            kitchenLeader
              ? `${kitchenLeader.key === "t0" ? teamText(0) : teamText(1)} had the best kitchen-rally estimate: ${kitchenLeader.wins}/${kitchenLeader.attempts} wins, ${fmtNumber(
                  kitchenLeader.pointsAdded,
                )} points added.`
              : "No serving kitchen-arrival rows were available.",
            `${rows.filter((row) => row.servingArrivalSuccess === true).length} rallies reached the current serving-team kitchen-arrival goal.`,
            "This section is only as good as the kitchen-arrival signal; it is useful for direction, not yet a human-charted truth table.",
          ]}
        />
      </Section>
    </Box>
  );
}

function statByKey(rows: StatRow[], key: string): StatRow | null {
  return rows.find((row) => row.key === key) ?? null;
}

function statSummaryText(row: StatRow | null): string {
  if (!row || row.attempts <= 0) return "no attempts";
  return `${pct(row.average)} on ${row.shot} (${row.points}/${row.attempts})`;
}

function OffensiveDiscussion(props: {
  model: PointsAddedModel;
  playerText: (playerId: PlayerId) => string;
  teamText: (team: Team01) => string;
}) {
  const { model, playerText, teamText } = props;
  const playerServeRows = PLAYER_IDS
    .map((playerId) => ({
      playerId,
      serve: statByKey(model.offensive, `p${playerId}:${roleFromShotNumber(1)}`),
      third: statByKey(model.offensive, `p${playerId}:${roleFromShotNumber(3)}`),
      fifth: statByKey(model.offensive, `p${playerId}:${roleFromShotNumber(5)}`),
    }))
    .filter((row) => row.serve || row.third || row.fifth);

  const topServe =
    [...playerServeRows]
      .filter((row) => row.serve && row.serve.attempts > 0)
      .sort((a, b) => (b.serve?.pointsAdded ?? -Infinity) - (a.serve?.pointsAdded ?? -Infinity))[0] ??
    null;
  const lowServe =
    [...playerServeRows]
      .filter((row) => row.serve && row.serve.attempts > 0)
      .sort((a, b) => (a.serve?.pointsAdded ?? Infinity) - (b.serve?.pointsAdded ?? Infinity))[0] ??
    null;
  const topThird =
    [...playerServeRows]
      .filter((row) => row.third && row.third.attempts > 0)
      .sort((a, b) => (b.third?.pointsAdded ?? -Infinity) - (a.third?.pointsAdded ?? -Infinity))[0] ??
    null;
  const topFifth =
    [...playerServeRows]
      .filter((row) => row.fifth && row.fifth.attempts > 0)
      .sort((a, b) => (b.fifth?.pointsAdded ?? -Infinity) - (a.fifth?.pointsAdded ?? -Infinity))[0] ??
    null;

  const team0Serve = statByKey(model.offensive, `t0:${roleFromShotNumber(1)}`);
  const team0Third = statByKey(model.offensive, `t0:${roleFromShotNumber(3)}`);
  const team0Fifth = statByKey(model.offensive, `t0:${roleFromShotNumber(5)}`);
  const team1Serve = statByKey(model.offensive, `t1:${roleFromShotNumber(1)}`);
  const team1Third = statByKey(model.offensive, `t1:${roleFromShotNumber(3)}`);
  const team1Fifth = statByKey(model.offensive, `t1:${roleFromShotNumber(5)}`);

  const goalLine = playerServeRows
    .map((row) => {
      const serve = row.serve;
      return serve
        ? `${playerText(row.playerId)}: ${pct(serve.goalAverage)}`
        : `${playerText(row.playerId)}: -`;
    })
    .join(" · ");

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        color: "#111",
        fontSize: 13,
        lineHeight: 1.45,
        mt: 0.5,
      }}
    >
      <TextBlock>
        This section reads the offensive side of the point: when a player took a
        serving-team shot, did that player&apos;s team eventually score? Using this
        game&apos;s observed serving-team scoring rate of{" "}
        {pct(model.scoringRate)}, attempts above that rate add raw points; attempts
        below it subtract raw points.
      </TextBlock>
      <TextBlock>
        Scoring Avg is Points divided by Attempts. Failed is the count of
        attempts tagged as a miss or fault by the hitter. Goal is broader than
        scoring: the serving team either scored the point or reached the kitchen;
        Goal Avg is Goal divided by Attempts.
      </TextBlock>
      {topServe?.serve ? (
        <TextBlock>
          The strongest serving line belongs to {playerText(topServe.playerId)}:{" "}
          {topServe.serve.points} points from {topServe.serve.attempts} serves (
          {pct(topServe.serve.average)}), worth {fmtNumber(topServe.serve.pointsAdded)} raw
          points added. {lowServe?.serve ? `${playerText(lowServe.playerId)} had the roughest serving line at ${pct(lowServe.serve.average)} (${lowServe.serve.points}/${lowServe.serve.attempts}), ${fmtNumber(lowServe.serve.pointsAdded)} raw points added.` : ""}
        </TextBlock>
      ) : null}
      <TextBlock>
        On serves, the current goal rates are {goalLine}. Team goal rates are{" "}
        {teamText(0)} at {pct(team0Serve?.goalAverage ?? null)}
        {" "}and {teamText(1)} at {pct(team1Serve?.goalAverage ?? null)}.
      </TextBlock>
      <TextBlock>
        Third-shot construction: {topThird?.third ? `${playerText(topThird.playerId)} has the best third-shot scoring line at ${pct(topThird.third.average)} (${topThird.third.points}/${topThird.third.attempts}), adding ${fmtNumber(topThird.third.pointsAdded)} raw points.` : "there is not enough third-shot data yet."} Fifth-shot continuation: {topFifth?.fifth ? `${playerText(topFifth.playerId)} has the best fifth-shot line at ${pct(topFifth.fifth.average)} (${topFifth.fifth.points}/${topFifth.fifth.attempts}), adding ${fmtNumber(topFifth.fifth.pointsAdded)} raw points.` : "there is not enough fifth-shot data yet."}
      </TextBlock>
      <TextBlock>
        Team summary: {teamText(0)} scored {statSummaryText(team0Serve)},{" "}
        {statSummaryText(team0Third)}, and {statSummaryText(team0Fifth)}. {teamText(1)} scored{" "}
        {statSummaryText(team1Serve)}, {statSummaryText(team1Third)}, and{" "}
        {statSummaryText(team1Fifth)} on the first, third, and fifth-shot cuts.
        The tables above continue past fifths; treat the whole section as directional
        because these rows still double-count a rally across multiple shot roles.
      </TextBlock>
    </Box>
  );
}

function StatsTable(props: {
  title: string;
  rows: StatRow[];
  pointHeader: string;
  averageHeader: string;
  subjectHeader?: string;
  note?: string;
  metricHeader?: string;
  metricHelp?: string;
}) {
  return (
    <Box>
      <TableTitle>{props.title}</TableTitle>
      {props.note ? (
        <Typography sx={{ mt: -0.25, mb: 0.65, color: "#111", fontSize: 12 }}>
          {props.note}
        </Typography>
      ) : null}
      <Box component="table" sx={tableSx(props.subjectHeader === "Team" ? 210 : 150)}>
        <thead>
          <tr>
            <th>{props.subjectHeader ?? "Player"}</th>
            <th>
              <PointsAddedHeader
                label={props.metricHeader}
                help={props.metricHelp}
              />
            </th>
            <th>Shot</th>
            <th>
              <HelpHeader
                label="Attempts"
                help="How many times this player or team had this shot role in the charted rows."
              />
            </th>
            <th>
              <HelpHeader
                label="Failed"
                help="How many of those attempts were tagged as a missed/faulted shot by the hitter."
              />
            </th>
            <th>{props.pointHeader}</th>
            <th>
              <HelpHeader
                label={props.averageHeader}
                help={
                  props.pointHeader.startsWith("Opp")
                    ? "Opp Points divided by attempts: how often the opponent ultimately scored after this player/team had this defensive shot role."
                    : "Points divided by attempts: how often the serving team ultimately scored after this player/team had this shot role."
                }
              />
            </th>
            <th>
              <HelpHeader
                label="Goal"
                help={
                  props.pointHeader.startsWith("Opp")
                    ? "Count of attempts where the opponent either scored the point or reached the kitchen."
                    : "Count of attempts where the serving team either scored the point or reached the kitchen."
                }
              />
            </th>
            <th>
              <HelpHeader
                label="Goal Avg"
                help={
                  props.pointHeader.startsWith("Opp")
                    ? "Goal divided by attempts: how often those attempts still let the opponent score or reach the kitchen."
                    : "Goal divided by attempts: how often those attempts produced either a point or kitchen arrival for the serving team."
                }
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {orderedStatRows(props.rows).map((row) => (
            <Box component="tr" key={row.key} sx={rowToneSx(row.pointsAdded)}>
              <td>{row.label}</td>
              <td>
                <PointsAddedCell value={row.pointsAdded} />
              </td>
              <td>{row.shot}</td>
              <td>{row.attempts}</td>
              <td>{row.failed}</td>
              <td>{row.points}</td>
              <td>{pct(row.average)}</td>
              <td>{row.goal}</td>
              <td>{pct(row.goalAverage)}</td>
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function ComboTable(props: {
  title: string;
  firstHeader: string;
  secondHeader: string;
  rows: ComboRow[];
  pointHeader: string;
  averageHeader: string;
  metricHeader?: string;
  metricHelp?: string;
}) {
  return (
    <Box>
      <TableTitle>{props.title}</TableTitle>
      <Box component="table" sx={tableSx()}>
        <thead>
          <tr>
            <th>{props.firstHeader}</th>
            <th>{props.secondHeader}</th>
            <th>
              <PointsAddedHeader
                label={props.metricHeader}
                help={props.metricHelp}
              />
            </th>
            <th>
              <HelpHeader
                label="Attempts"
                help="How many times this player pairing appeared in the charted rows."
              />
            </th>
            <th>
              <HelpHeader
                label="Failed"
                help="How many pairing attempts ended with the second listed shot tagged as a miss or fault."
              />
            </th>
            <th>{props.pointHeader}</th>
            <th>
              <HelpHeader
                label={props.averageHeader}
                help="Points divided by attempts for this pairing."
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {orderedComboRows(props.rows).map((row, index, sortedRows) => {
            const previous = sortedRows[index - 1] ?? null;
            const needsDivider = previous != null && previous.firstTeam !== row.firstTeam;
            return (
              <React.Fragment key={row.key}>
                {needsDivider ? (
                  <tr aria-hidden="true">
                    <td colSpan={7} style={{ padding: 0, height: 6, border: 0 }} />
                  </tr>
                ) : null}
                <Box component="tr" sx={rowToneSx(row.pointsAdded)}>
                  <td>{row.first}</td>
                  <td>{row.second}</td>
                  <td>
                    <PointsAddedCell value={row.pointsAdded} />
                  </td>
                  <td>{row.attempts}</td>
                  <td>{row.failed}</td>
                  <td>{row.points}</td>
                  <td>{pct(row.average)}</td>
                </Box>
              </React.Fragment>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

function KitchenTable(props: { rows: KitchenRow[] }) {
  return (
    <Box>
      <TableTitle>Kitchen Success</TableTitle>
      <Box component="table" sx={tableSx()}>
        <thead>
          <tr>
            <th>Team</th>
            <th>Attempts</th>
            <th>Wins</th>
            <th>Win Point Avg</th>
            <th><PointsAddedHeader /></th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <Box component="tr" key={row.key} sx={rowToneSx(row.pointsAdded)}>
              <td>{row.label}</td>
              <td>{row.attempts}</td>
              <td>{row.wins}</td>
              <td>{pct(row.average)}</td>
              <td>
                <PointsAddedCell value={row.pointsAdded} />
              </td>
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function SummaryTable(props: {
  rows: Array<{
    playerId: PlayerId;
    sortOrder: number;
    total: number;
    raw: Record<string, number>;
    offense: number;
    defense: number;
  }>;
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
}) {
  const roles = sortedRolesFromSummaries(props.rows);
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{ ...tableSx(), tableLayout: "auto", minWidth: 520 + roles.length * 112 }}
      >
        <thead>
          <tr>
            <th>Player</th>
            <th><PointsAddedHeader label="Average Pts Added" /></th>
            {roles.map((role) => (
              <th key={role}>
                <PointsAddedHeader
                  label={`${ordinal(roleShotNumber(role))} ${isCreatedRole(role) ? "Created" : "Prevented"}`}
                  help={
                    isCreatedRole(role)
                      ? "Raw contribution for this serving-team shot number before averaging."
                      : "Raw prevention for this receiving-team shot number before averaging."
                  }
                />
              </th>
            ))}
            <th>Average Created</th>
            <th>Average Prevented</th>
          </tr>
        </thead>
        <tbody>
          {[...props.rows].sort((a, b) => a.sortOrder - b.sortOrder).map((row) => (
            <Box component="tr" key={row.playerId} sx={rowToneSx(row.total)}>
              <td>{props.playerBadge(row.playerId, 30)}</td>
              <td>
                <PointsAddedCell value={row.total} />
              </td>
              {roles.map((role) => (
                <td key={role}>
                  <PointsAddedCell value={rawValue(row.raw, role)} />
                </td>
              ))}
              <td>
                <PointsAddedCell value={row.offense} />
              </td>
              <td>
                <PointsAddedCell value={row.defense} />
              </td>
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function EntryTable(props: {
  rows: Array<{
    key: string;
    row: KitchenArrivalRow;
    shots: Record<number, PlayerId | null>;
    missedShotNumber: number | null;
  }>;
  shotNumbers: number[];
  playerBadge: (
    playerId: number | null | undefined,
    size?: number,
    missed?: boolean,
  ) => React.ReactNode;
  teamBadge: (team: Team01) => React.ReactNode;
  teamForPlayer: (playerId: PlayerId) => Team01;
  onOpenRallyShot?: (payload: OpenShotPreviewPayload) => void;
}) {
  const shotCell = (
    row: KitchenArrivalRow,
    playerId: PlayerId | null,
    shotNumber: number,
    missedShotNumber: number | null,
  ) => {
    if (missedShotNumber != null && shotNumber > missedShotNumber) return "-";
    const content = props.playerBadge(playerId, 26, missedShotNumber === shotNumber);
    if (!props.onOpenRallyShot || playerId == null) return content;
    const timing = shotLoopForRow(row, shotNumber, missedShotNumber);
    const title = `Rally ${row.rallyIndex + 1} · Shot ${shotNumber}`;
    const context = `${shotNumberLabel(shotNumber)} by Team ${
      props.teamForPlayer(playerId) + 1
    }`;
    const exportWatchKind = shotNumber % 2 === 1 ? "created" : "prevented";
    const exportWatchId = `${row.rallyIndex}:${shotNumber}:${playerId}:${exportWatchKind}`;
    return (
      <Tooltip title={`${title}: ${context}`} arrow placement="top">
        <Button
          size="small"
          variant="text"
          data-pa-watch-id={exportWatchId}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            props.onOpenRallyShot?.({
              row,
              shotNumber,
              title,
              context,
              timeSec: timing.timeSec,
              loop: timing.loop,
            });
          }}
          sx={{
            minWidth: 0,
            p: 0,
            color: "inherit",
            lineHeight: 1,
            textTransform: "none",
          }}
        >
          {content}
        </Button>
      </Tooltip>
    );
  };

  return (
    <Box className="points-added-entry-scroll" sx={{ maxHeight: 360, overflow: "auto" }}>
      <Box
        component="table"
        sx={{
          ...tableSx(),
          tableLayout: "auto",
          minWidth: Math.max(760, 360 + props.shotNumbers.length * 92),
        }}
      >
        <thead>
          <tr>
            {[
              "Rally",
              "Starting score",
              "Ending score",
              ...props.shotNumbers.map((shotNumber) => ordinal(shotNumber)),
              "Kitchen",
              "Point",
            ].map((header) => (
              <Box
                component="th"
                key={header}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  bgcolor: "#dcf4d3",
                }}
              >
                {header}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map(({ key, row, shots, missedShotNumber }) => {
            const awarded = pointTeam(row);
            return (
              <tr key={key}>
                <td>{row.rallyIndex + 1}</td>
                <td>{scorePairText(row.scoreBefore)}</td>
                <td>{scorePairText(row.scoreAfter)}</td>
                {props.shotNumbers.map((shotNumber) => (
                  <td key={shotNumber}>
                    {shotCell(row, shots[shotNumber] ?? null, shotNumber, missedShotNumber)}
                  </td>
                ))}
                <td>
                  <Box
                    component="span"
                    sx={{
                      color:
                        row.servingArrivalSuccess === true
                          ? "#008000"
                          : row.servingArrivalSuccess === false
                            ? "#c00000"
                            : "#555",
                      fontWeight: 900,
                    }}
                  >
                    {row.servingArrivalSuccess === true
                      ? "Yes"
                      : row.servingArrivalSuccess === false
                        ? "No"
                        : "-"}
                  </Box>
                </td>
                <td>{awarded == null ? "-" : `Team ${awarded + 1}`}</td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}
